import React, { useEffect, useState, useRef, Children, createElement } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, PhoneIcon, MailIcon, MapPinIcon, ShieldCheckIcon, TargetIcon, TrendingUpIcon, ZapIcon, UsersIcon, BarChart3Icon, GlobeIcon, StarIcon, ClockIcon, DollarSignIcon, AlertTriangleIcon, ArrowRightIcon, CalendarIcon } from 'lucide-react';
import { NavigationHeader } from '../components/NavigationHeader';
import { ProofCarousel } from '../components/ProofCarousel';
import { EcgBackground } from '../components/EcgBackground';
import emailjs from '@emailjs/browser';
// Initialize EmailJS with public key
emailjs.init('vWvHy5XmFe6E1h_h6');
// ── Meta Pixel (Facebook) ──
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: (...args: any[]) => void;
  }
}
;
(function () {
  if (typeof window !== 'undefined' && !window.fbq) {
    const n: any = window.fbq = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [] as any[];
    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const s = document.getElementsByTagName('script')[0];
    s.parentNode?.insertBefore(t, s);
  }
})();
window.fbq('init', '1350274987135662');
// Custom professional rejection icon
function RejectIcon({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-318">
      <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.5" data-id="element-319" />
      <path d="M7 7l6 6M13 7l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-320" />
    </svg>;
}
const EMAILJS_SERVICE_ID = 'service_5st6tfj';
const EMAILJS_TEMPLATE_ADMIN = 'template_kwi4ynr';
const EMAILJS_TEMPLATE_AUTOREPLY = 'template_2deikke';
const EMAILJS_PUBLIC_KEY = 'vWvHy5XmFe6E1h_h6';
const BACKGROUND_IMAGE = 'https://cdn.magicpatterns.com/uploads/uzJHBZopyDZuY9WG3op2uA/heylloyd-image-1772589998648.png';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/8FJuYkRa3w5u3JjEiR3Wxx/A3ACF160-490D-4B87-A543-339A13041014.png';
const OFFICE_IMAGE = 'https://cdn.magicpatterns.com/uploads/i3vtP4KwJtKvGunCwNHaR1/A3ACF160-490D-4B87-A543-339A13041014.png';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
function CTAButton({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Meta Pixel: Track Lead event (user clicked CTA)
    if (window.fbq) window.fbq('track', 'Lead');
    const formSection = document.getElementById('formulario');
    if (formSection) {
      formSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <motion.a href="#formulario" onClick={handleClick} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} className={`
        inline-flex items-center justify-center gap-2 gold-gradient text-black font-bold rounded-full
        border border-transparent emerald-glow-hover transition-all duration-300
        px-6 py-4 text-sm sm:px-10 sm:py-5 sm:text-lg md:text-xl
        max-w-full whitespace-nowrap text-center cursor-pointer
        ${className}
      `} data-id="element-321">
      {children}
      <ArrowRightIcon className="w-5 h-5 flex-shrink-0" data-id="element-322" />
    </motion.a>;
}
function CTABlock() {
  return <div className="flex flex-col items-center gap-3 w-full" data-id="element-323">
      <CTAButton data-id="element-324">QUIERO MI DIAGNÓSTICO GRATIS</CTAButton>
      <a href="https://calendar.app.google/PYPdcRoYne7QrNJA7" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#D4A843]/70 hover:text-[#D4A843] text-sm transition-colors" data-id="element-325">
        <CalendarIcon className="w-3.5 h-3.5" data-id="element-326" />
        <span data-id="element-327">Reservar una cita para mi diagnóstico gratis</span>
      </a>
    </div>;
}
function SectionWrapper({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  return <motion.section id={id} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-100px'
  }} variants={fadeInUp} className={`relative py-14 md:py-18 px-4 md:px-8 overflow-hidden ${className}`} data-id="element-328">
      <div className="relative z-10 max-w-6xl mx-auto" data-id="element-329">{children}</div>
    </motion.section>;
}
function StickyBar() {
  return <div className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#D4A843]/20" data-id="element-330">
      <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-331">
        <p className="text-white text-sm md:text-base text-center sm:text-left" data-id="element-332">
          <span className="gold-text font-semibold" data-id="element-333">
            ¿Quieres más clientes esta semana?
          </span>{' '}
          Agenda tu Asesoría Gratis
        </p>
        <motion.a href="#formulario" whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} className="gold-gradient text-black font-bold px-6 py-2 rounded-md text-sm whitespace-nowrap" data-id="element-334">
          QUIERO MÁS CLIENTES
        </motion.a>
      </div>
    </div>;
}
function SectionDivider() {
  return <div className="w-full h-px bg-gray-800/50" data-id="element-335" />;
}
function BackgroundSection({
  children,
  className = '',
  id = '',
  overlay = 'bg-[#0A0A0A]/90'
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
  overlay?: string;
}) {
  return <motion.section id={id} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-100px'
  }} variants={fadeInUp} className={`relative py-14 md:py-18 px-4 md:px-8 overflow-hidden ${className}`} style={{
    backgroundColor: '#0A0A0A'
  }} data-id="element-336">
      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto" data-id="element-337">{children}</div>
    </motion.section>;
}
function HeroSection() {
  const bulletPoints = ['Si tus anuncios "tienen likes" pero no generan llamadas', 'Si ya "probaste de todo" y sientes que te vieron la cara', 'Si tu competencia no es mejor… pero se está quedando con tus clientes', 'Si estás cansado de oír: "dale tiempo" mientras las cuentas siguen llegando', 'Si te dicen "espera 3 meses y verás resultados", y cada mes vuelves a pagar la factura de tu gurú que vende más esperanza que clientes', 'Si estás cansado de canjear tus servicios a cambio de conseguir clientes, y los influencers no generan clientes reales, solo interacción'];
  const stats = [{
    value: '1,877+',
    label: 'Negocios escalados en USA',
    icon: TrendingUpIcon
  }, {
    value: '30 días',
    label: 'Garantía de resultados',
    icon: ShieldCheckIcon
  }, {
    value: 'Bilingüe',
    label: 'Hablamos inglés y español',
    icon: GlobeIcon
  }, {
    value: 'Tampa, FL',
    label: 'Oficinas físicas',
    icon: MapPinIcon
  }];
  return <>
      {/* ═══ ABOVE THE FOLD — Premium Agency Layout ═══ */}
      <section className="relative overflow-hidden bg-[#0A0A0A]" data-id="element-338">
        {/* ECG Background — Hero */}
        <EcgBackground intensity="subtle" position="bottom" speed={10} data-id="element-339" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pt-36 md:pt-44 pb-10 md:pb-14" data-id="element-340">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="flex flex-col items-center" data-id="element-341">
            {/* Badge */}
            <motion.div variants={staggerItem} className="mb-6" data-id="element-342">
              <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#10B981]/30 bg-[#059669]/10 text-[#10B981] text-xs font-semibold tracking-widest uppercase shadow-[0_0_12px_rgba(5,150,105,0.25)]" data-id="element-343">
                <ZapIcon className="w-3.5 h-3.5" data-id="element-344" />
                Negocios locales en USA
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1 variants={staggerItem} className="text-center text-[2rem] sm:text-[2.75rem] md:text-[3.5rem] lg:text-[4rem] font-black leading-[1.1] tracking-tight text-white max-w-4xl mb-8" data-id="element-345">
              Si ya pagaste anuncios, contrataste un "community manager" o
              seguiste a un gurú… y aún así{' '}
              <span className="gold-text" data-id="element-346">
                NO consigues tener más clientes diarios y constantes
              </span>
              , esto es para ti.
            </motion.h1>

            {/* Office Image — Right after headline */}
            <motion.div variants={staggerItem} className="w-full max-w-4xl mx-auto mb-8" data-id="element-347">
              <div className="relative" data-id="element-348">
                <div className="absolute -inset-px rounded-xl bg-gradient-to-b from-[#D4A843]/20 via-[#D4A843]/5 to-transparent" data-id="element-349" />
                <img src={OFFICE_IMAGE} alt="Oficina Doctor Ecommerce — Tampa, FL" className="relative w-full rounded-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]" data-id="element-350" />
              </div>
            </motion.div>

            {/* Subtitle */}
            <motion.div variants={staggerItem} className="max-w-3xl mx-auto mb-8" data-id="element-351">
              <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed mb-4" style={{
              textAlign: 'justify'
            }} data-id="element-352">
                No te faltan ganas. Te falta un{' '}
                <span className="text-white font-semibold" data-id="element-353">
                  sistema que haga que las personas correctas te encuentren, te
                  llamen y te contraten TODOS LOS DÍAS.
                </span>
              </p>
              <p className="text-sm sm:text-base md:text-lg text-gray-400 leading-relaxed" style={{
              textAlign: 'justify'
            }} data-id="element-354">
                Nosotros no vendemos "branding", "contenido bonito" ni hacks.{' '}
                <span className="text-white font-semibold" data-id="element-355">
                  Te instalamos un sistema de marketing propio que hace que las
                  personas correctas te encuentren, te llamen y te contraten
                  TODOS LOS DÍAS.
                </span>{' '}
                No revendemos leads ni compartimos contactos. Cada cliente que
                llega, llega porque vio TU negocio, TU oferta, y quiere
                contratarte a TI.
              </p>
            </motion.div>

            {/* CTA */}
            <motion.div variants={staggerItem} className="mb-6" data-id="element-356">
              <CTABlock data-id="element-357" />
            </motion.div>

            {/* Trust indicators */}
            <motion.div variants={staggerItem} className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-gray-500 text-xs md:text-sm" data-id="element-358">
              <span className="flex items-center gap-1.5" data-id="element-359">
                <GlobeIcon className="w-3.5 h-3.5 text-[#D4A843]/70" data-id="element-360" />
                Clientes en todos los Estados Unidos
              </span>
              <span className="flex items-center gap-1.5" data-id="element-361">
                <MapPinIcon className="w-3.5 h-3.5 text-[#D4A843]/70" data-id="element-362" />
                Oficinas físicas en Tampa, FL
              </span>
              <span className="flex items-center gap-1.5" data-id="element-363">
                <ClockIcon className="w-3.5 h-3.5 text-[#D4A843]/70" data-id="element-364" />
                Respuesta en menos de 2h
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══ STATS BAR ═══ */}
      <div className="border-y border-[#D4A843]/20 bg-[#0A0A0A]" data-id="element-365">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:py-8" data-id="element-366">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8" data-id="element-367">
            {stats.map((stat, index) => <motion.div key={index} variants={staggerItem} className="text-center relative" data-id="element-368">
                {/* Vertical divider between items (hidden on first) */}
                {index > 0 && <div className="absolute left-0 top-1/2 -translate-y-1/2 h-12 w-px bg-gradient-to-b from-transparent via-[#D4A843]/20 to-transparent hidden md:block" data-id="element-369" />}
                <div className="flex justify-center mb-3" data-id="element-370">
                  <div className="w-10 h-10 rounded-lg bg-[#D4A843]/10 border border-[#D4A843]/20 flex items-center justify-center" data-id="element-371">
                    <stat.icon className="w-5 h-5 text-[#D4A843]" data-id="element-372" />
                  </div>
                </div>
                <p className="text-2xl md:text-3xl font-black text-white tracking-tight" data-id="element-373">
                  {stat.value}
                </p>
                <p className="text-xs md:text-sm text-gray-400 mt-1 font-medium" data-id="element-374">
                  {stat.label}
                </p>
              </motion.div>)}
          </div>
        </motion.div>
      </div>

      {/* ═══ PAIN POINTS GRID ═══ */}
      <section className="bg-[#0A0A0A] py-16 md:py-24 px-4 sm:px-6" data-id="element-375">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} variants={staggerContainer} className="max-w-4xl mx-auto" data-id="element-376">
          {/* Section Header */}
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-377">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase mb-6" data-id="element-378">
              <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-379" />
              Diagnóstico
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4 max-w-3xl mx-auto leading-tight" data-id="element-380">
            Esto le está pasando a tu negocio{' '}
            <span className="gold-text" data-id="element-381">
              y cada día que pasa te cuesta dinero
            </span>
          </motion.h2>

          <motion.p variants={staggerItem} className="text-gray-500 text-center text-sm md:text-base mb-10" data-id="element-382">
            No es mala suerte. Es un problema de sistema — y tiene solución.
          </motion.p>

          {/* Pain Points — Premium Cards */}
          <div className="space-y-4 md:space-y-5 mb-12" data-id="element-383">
            {[{
            icon: BarChart3Icon,
            text: 'Tus anuncios "tienen likes" pero',
            highlight: 'no generan llamadas',
            detail: 'Interacción sin conversión es solo entretenimiento. No es marketing.'
          }, {
            icon: ShieldCheckIcon,
            text: 'Ya "probaste de todo" y sientes que',
            highlight: 'te vieron la cara',
            detail: 'Agencias, freelancers, cursos… y el resultado siempre fue el mismo: nada.'
          }, {
            icon: TrendingUpIcon,
            text: 'Tu competencia no es mejor, pero',
            highlight: 'se está quedando con tus clientes',
            detail: 'No gana el mejor servicio. Gana el que tiene mejor sistema de adquisición.'
          }, {
            icon: ClockIcon,
            text: 'Estás cansado de oír "dale tiempo" mientras',
            highlight: 'las cuentas siguen llegando',
            detail: 'La paciencia no paga la renta. Necesitas resultados medibles ahora.'
          }, {
            icon: DollarSignIcon,
            text: 'Te dicen "espera 3 meses" y cada mes vuelves a pagar la factura de un gurú que',
            highlight: 'vende más esperanza que clientes',
            detail: 'Si después de 90 días no hay ROI, el problema no eres tú.'
          }, {
            icon: UsersIcon,
            text: 'Canjeas servicios a cambio de clientes, y los influencers',
            highlight: 'no generan reservaciones ni llamadas',
            detail: 'Tráfico e interacción no son ventas. Sin citas, no hay negocio.'
          }].map((point, index) => <motion.div key={index} variants={staggerItem} className="group relative bg-[#070707] border border-gray-800 rounded-xl overflow-hidden transition-all duration-300 hover:border-[#10B981] hover:bg-[#059669]/[0.06] hover:shadow-[0_0_16px_rgba(5,150,105,0.2)]" data-id="element-384">
                <div className="flex items-start gap-4 md:gap-5 p-5 md:p-6" data-id="element-385">
                  {/* Icon */}
                  <div className="flex-shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-lg bg-[#D4A843]/10 border border-[#D4A843]/20 flex items-center justify-center mt-0.5" data-id="element-386">
                    <point.icon className="w-5 h-5 text-[#D4A843]" data-id="element-387" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-388">
                    <p className="text-gray-300 text-sm md:text-base leading-relaxed" data-id="element-389">
                      {point.text}{' '}
                      <span className="text-white font-bold" data-id="element-390">
                        {point.highlight}.
                      </span>
                    </p>
                    <p className="text-gray-500 text-xs md:text-sm mt-2 leading-relaxed" data-id="element-391">
                      {point.detail}
                    </p>
                  </div>

                  {/* Number */}
                  <span className="flex-shrink-0 text-[#D4A843]/20 font-black text-2xl md:text-3xl leading-none select-none" data-id="element-392">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
              </motion.div>)}
          </div>

          {/* Proof Badge */}
          <motion.div variants={staggerItem} className="relative max-w-lg mx-auto mb-12" data-id="element-393">
            <div className="relative bg-[#070707] border border-gray-800 rounded-xl p-6 md:p-8 text-center" data-id="element-394">
              <div className="flex justify-center mb-4" data-id="element-395">
                <div className="w-12 h-12 rounded-full gold-gradient flex items-center justify-center" data-id="element-396">
                  <TargetIcon className="w-6 h-6 text-black" data-id="element-397" />
                </div>
              </div>
              <p className="text-white font-bold text-lg md:text-xl mb-2" data-id="element-398">
                Sistema de Adquisición de Clientes
              </p>
              <p className="gold-text font-semibold text-sm md:text-base mb-1" data-id="element-399">
                Para negocios locales en USA — 100% en español
              </p>
              <p className="text-gray-500 text-sm" data-id="element-400">
                Enfocado en lo único que importa: llamadas, citas y ventas
              </p>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div variants={staggerItem} className="text-center" data-id="element-401">
            <div className="flex justify-center mb-5" data-id="element-402">
              <CTABlock data-id="element-403" />
            </div>
            <p className="text-gray-600 text-xs md:text-sm italic max-w-md mx-auto leading-relaxed" data-id="element-404">
              Sin compromiso. Te decimos la verdad, lo que necesitas implementar
              y ejecutar paso a paso, aunque no trabajes con nosotros.
            </p>
          </motion.div>
        </motion.div>
      </section>
    </>;
}
function ProblemSection() {
  const problems = [{
    title: 'Tus anuncios no sirven',
    description: 'No cualquiera que sabe abrir el Administrador de Anuncios de Meta es un experto. Cualquiera puede prender un anuncio. Lo difícil es que ese anuncio traiga clientes. Si llevas semanas o meses pagando publicidad y los resultados no se ven en tu bolsillo, la respuesta es simple: los anuncios no sirven y quien los hace no sabe lo que está haciendo.',
    icon: BarChart3Icon
  }, {
    title: 'Tus creativos no están segmentados al público correcto',
    description: 'Si el anuncio no le habla a la persona correcta, Meta te cobra más por mostrárselo a más gente hasta encontrar a alguien que reaccione. Y si nadie reacciona, el costo sube. Y sigue subiendo. Y tú sigues pagando. Sin métricas reales, no hay crecimiento real.',
    icon: TargetIcon
  }, {
    title: 'Tú eres el dueño del negocio — ya tienes suficiente',
    description: 'Intentar manejar tu propia publicidad por encima de todo lo demás tiene un solo final: el cansancio te gana, los resultados no llegan y terminas abandonando. El ecosistema detrás de una campaña publicitaria que realmente convierte es mucho más grande y complejo de lo que nadie te explicó.',
    icon: UsersIcon
  }, {
    title: 'Tu landing page dice mucho… pero no comunica nada',
    description: 'Tu página web no genera autoridad. Se ve genérica. Se siente como cualquier otra. Y cuando alguien llega ahí y te ve igual que a todos los demás, la pregunta que se hace es inevitable: ¿por qué tendría que contratarte a ti?',
    icon: GlobeIcon
  }, {
    title: 'No hay estructura operativa detrás del negocio',
    description: 'No hay procesos definidos. No hay roles claros. No hay sistemas. Todo depende del dueño haciendo todo al mismo tiempo. Tanto esfuerzo, tanto dinero invertido en conseguir un lead… y cuando por fin llega uno, el negocio no está listo para darle la mejor versión de sí mismo.',
    icon: ZapIcon
  }, {
    title: 'Cero medición real — cada error se paga en efectivo',
    description: 'CTR. CPM. ROAS. CPC. Suenan técnicos. Pero si tu negocio no está escalando, la verdad es otra: no solo no entienden esas métricas, no saben lo que están haciendo. Están apostando con tu dinero. Cada decisión equivocada se mide en dólares que salieron de tu bolsillo y no volvieron.',
    icon: DollarSignIcon
  }];
  const blameExcuses = ['Tu nicho está saturado', 'Tu zona es difícil', 'Necesitas más presupuesto', 'Tu oferta no está lista', 'Es que tienes que hacer mejor contenido…'];
  return <>
      <SectionDivider data-id="element-405" />
      <SectionWrapper className="bg-[#111]" data-id="element-406">
        {/* ECG Background — Problem/Analysis */}
        <EcgBackground intensity="subtle" position="bottom" speed={11} bgColor="#111" data-id="element-407" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-408">
          {/* Section Badge */}
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-409">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-410">
              <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-411" />
              Análisis
            </span>
          </motion.div>

          {/* Title Block */}
          <motion.h2 variants={staggerItem} className="text-3xl md:text-5xl text-center mb-4 max-w-4xl mx-auto leading-tight" data-id="element-412">
            La razón por la que{' '}
            <span className="gold-text" data-id="element-413">NO te funcionó antes</span>
          </motion.h2>
          <motion.p variants={staggerItem} className="text-lg md:text-xl text-gray-400 text-center mb-2" data-id="element-414">
            (y no es porque tu negocio sea malo)
          </motion.p>
          <motion.p variants={staggerItem} className="text-base md:text-lg text-gray-500 text-center mb-16 md:mb-20" data-id="element-415">
            Ni porque necesites más capital o un branding nuevo
          </motion.p>

          {/* Truth Bomb — Editorial Pull Quote */}
          <motion.div variants={staggerItem} className="max-w-3xl mx-auto mb-12" data-id="element-416">
            <div className="relative" data-id="element-417">
              {/* Decorative gold line */}
              <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#D4A843] via-[#D4A843]/40 to-transparent" data-id="element-418" />

              <div className="pl-8 md:pl-10" data-id="element-419">
                <p className="text-lg md:text-xl text-gray-300 mb-6 leading-relaxed" style={{
                textAlign: 'justify'
              }} data-id="element-420">
                  Te voy a decir algo que duele, pero te libera:
                </p>
                <p className="text-lg md:text-xl text-white mb-8 leading-relaxed" style={{
                textAlign: 'justify'
              }} data-id="element-421">
                  <span className="gold-text font-semibold" data-id="element-422">
                    La mayoría de "agencias" y "gurús" no fracasan por mala
                    intención. Fracasan porque su modelo está diseñado para
                    cobrarte… no para que tú ganes.
                  </span>
                </p>

                <p className="text-gray-400 mb-5 text-sm uppercase tracking-widest" data-id="element-423">
                  Y cuando no funciona, te culpan a ti:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-6" data-id="element-424">
                  {blameExcuses.map((excuse, index) => <div key={index} className="flex items-center gap-3" data-id="element-425">
                      <RejectIcon className="w-4 h-4 text-red-500/70 flex-shrink-0" data-id="element-426" />
                      <span className="text-gray-400 text-sm md:text-base" data-id="element-427">
                        "{excuse}"
                      </span>
                    </div>)}
                </div>

                <p className="text-gray-500 italic text-sm" data-id="element-428">
                  Traducción: págame otro mes y ojalá pase algo.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Problems — Editorial Numbered List */}
          <motion.div variants={staggerItem} className="max-w-4xl mx-auto mb-12" data-id="element-429">
            <div className="space-y-0" data-id="element-430">
              {problems.map((problem, index) => <motion.div key={index} variants={staggerItem} className="group" data-id="element-431">
                  {/* Top divider (only for first item) */}
                  {index === 0 && <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent" data-id="element-432" />}

                  <div className="py-8 md:py-10" data-id="element-433">
                    <div className="flex gap-6 md:gap-8" data-id="element-434">
                      {/* Large editorial number */}
                      <div className="flex-shrink-0 flex flex-col items-center gap-3" data-id="element-435">
                        <span className="text-[#D4A843]/15 font-black text-4xl md:text-5xl leading-none select-none" data-id="element-436">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <div className="w-9 h-9 rounded-lg bg-[#D4A843]/8 border border-[#D4A843]/15 flex items-center justify-center" data-id="element-437">
                          <problem.icon className="w-4 h-4 text-[#D4A843]/60" data-id="element-438" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 pt-1" data-id="element-439">
                        <h3 className="text-xl md:text-2xl font-bold mb-3 text-white leading-tight group-hover:text-[#D4A843] transition-colors duration-300" data-id="element-440">
                          {problem.title}
                        </h3>
                        <p className="text-gray-400 text-sm md:text-base leading-relaxed max-w-2xl" style={{
                      textAlign: 'justify'
                    }} data-id="element-441">
                          {problem.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Bottom divider */}
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent" data-id="element-442" />
                </motion.div>)}
            </div>
          </motion.div>

          {/* Closing Statement — Premium Block */}
          <motion.div variants={staggerItem} className="max-w-3xl mx-auto text-center" data-id="element-443">
            {/* Decorative separator */}
            <div className="flex items-center justify-center gap-4 mb-12" data-id="element-444">
              <div className="w-16 h-px bg-gradient-to-r from-transparent to-[#D4A843]/60" data-id="element-445" />
              <div className="w-2 h-2 rotate-45 border border-[#D4A843]/60" data-id="element-446" />
              <div className="w-16 h-px bg-gradient-to-l from-transparent to-[#D4A843]/60" data-id="element-447" />
            </div>

            {/* Heading */}
            <p className="text-2xl md:text-3xl lg:text-4xl font-bold mb-10 leading-tight text-center" data-id="element-448">
              Y al final, solo hay una verdad{' '}
              <span className="gold-text" data-id="element-449">que importa</span>
            </p>

            {/* Premium quote card */}
            <div className="relative mb-10" data-id="element-450">
              {/* Ambient glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-[#D4A843] opacity-[0.04] blur-[80px] rounded-full pointer-events-none" data-id="element-451" />

              {/* Outer gradient border */}
              <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-b from-[#D4A843]/40 via-[#D4A843]/15 to-[#D4A843]/5" data-id="element-452" />

              {/* Card */}
              <div className="relative bg-[#0A0A0A] rounded-2xl p-10 md:p-14" data-id="element-453">
                {/* Inner top glow */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-[#D4A843]/[0.06] via-transparent to-transparent pointer-events-none" data-id="element-454" />

                {/* Decorative quote mark */}
                <div className="absolute top-6 left-8 md:top-8 md:left-10" data-id="element-455">
                  <span className="text-[#D4A843]/10 text-6xl md:text-8xl font-serif leading-none select-none" data-id="element-456">
                    "
                  </span>
                </div>

                {/* Quote content */}
                <p className="relative text-lg md:text-xl lg:text-2xl text-gray-200 leading-relaxed text-center" style={{
                textAlign: 'justify'
              }} data-id="element-457">
                  <span className="gold-text font-semibold" data-id="element-458">
                    Si tu gurú, tu agencia o tu "experto en marketing" supiera
                    lo que hace, ya tendrías un sistema instalado que haga que
                    los clientes lleguen a tu puerta todos los días, queriendo
                    tus productos o servicios.
                  </span>
                </p>

                {/* Decorative closing quote mark */}
                <div className="absolute bottom-4 right-8 md:bottom-6 md:right-10" data-id="element-459">
                  <span className="text-[#D4A843]/10 text-6xl md:text-8xl font-serif leading-none select-none rotate-180 inline-block" data-id="element-460">
                    "
                  </span>
                </div>
              </div>
            </div>

            {/* Closing statement with emphasis */}
            <div className="text-center" data-id="element-461">
              <p className="text-xl md:text-2xl lg:text-3xl font-black tracking-tight" data-id="element-462">
                <span className="gold-text" data-id="element-463">Nosotros arreglamos todo eso</span>{' '}
                <span className="text-white" data-id="element-464">como un sistema.</span>
              </p>
              <div className="w-24 h-[2px] gold-gradient mx-auto mt-6 opacity-60" data-id="element-465" />
            </div>
          </motion.div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function SystemSection() {
  const phases = [{
    icon: TargetIcon,
    title: 'FASE 1 — ATRACCIÓN',
    subtitle: 'Llevamos a tu puerta a la persona que ya está lista para comprar.',
    description: 'No buscamos audiencias frías que nunca han pensado en tu servicio. Usamos inteligencia de comportamiento, datos de intención y segmentación quirúrgica para encontrar exactamente a la persona que hoy, en este momento, necesita lo que tú ofreces. Gente lista. No curiosos. No seguidores. Compradores.'
  }, {
    icon: ZapIcon,
    title: 'FASE 2 — CONVERSIÓN',
    subtitle: 'Convertimos ese interés en una llamada, una cita o una venta.',
    description: 'Construimos el puente completo: una propuesta diseñada específicamente para tu negocio, una experiencia que genera confianza en segundos, una oferta estructurada para que el cliente sienta que sería un error no actuar hoy, y el diferenciador real de tu negocio que llevas años teniendo… pero que nadie ha sabido comunicar como se debe.'
  }, {
    icon: TrendingUpIcon,
    title: 'FASE 3 — ESCALA',
    subtitle: 'Construimos el sistema para que los clientes sigan llegando, predecibles y en volumen.',
    description: 'Una vez que las Fases 1 y 2 están funcionando, el trabajo se convierte en sistema. La comunicación personalizada mantiene tu negocio presente en la mente del cliente correcto, en el momento correcto. Tu negocio se posiciona como la primera opción en tu zona. Tú haces lo que mejor sabes hacer: tu trabajo.'
  }];
  const industries = ['Roofing', 'Landscaping', 'Moving', 'Handyman', 'Salones y Barberías', 'Spa y Estética', 'Restaurantes y Food Trucks', 'Limpieza', 'Contadores y Taxes', 'Abogados', 'Médicos y Cirujanos', 'Beauty Services', 'Pet Grooming', 'Bakeries y Catering', 'Home Remodeling', 'Auto Repair', 'Real Estate', 'Plumbing', 'HVAC', 'Electricistas', 'Pintura', 'Pest Control', 'Towing / Grúas', 'Dentistas', 'Fitness y Gym', 'Seguros', 'Flooring / Pisos', 'Pressure Washing', 'Junk Removal', 'Car Wash y Detailing', 'Fotografía y Video', 'Eventos y Planning', 'Transporte / Trucking', 'Window Tinting', 'Cerrajería', 'Guardería / Daycare'];
  const differentiators = ['No dependemos de viralidad ni seguidores', 'No vendemos "presencia digital" — instalamos un sistema que hace que los clientes lleguen a tu puerta', 'No es una plantilla genérica — se construye sobre tu negocio específico', 'No requiere que tú seas el experto — para eso estamos nosotros', 'Cada pieza del sistema trabaja junta — no son partes sueltas', 'Se mide en resultados, no en reportes'];
  return <>
      <SectionDivider data-id="element-466" />
      <BackgroundSection overlay="bg-[#111]/95" data-id="element-467">
        {/* ECG Background — System A.C.E. */}
        <EcgBackground intensity="medium" position="center" speed={8} bgColor="#111" data-id="element-468" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-469">
          <motion.div variants={staggerItem} className="text-center mb-16" data-id="element-470">
            <p className="text-[#D4A843] text-lg mb-4" data-id="element-471">
              ¿Qué hacemos realmente?
            </p>
            <h2 className="text-3xl md:text-5xl mb-4" data-id="element-472">
              No es marketing. Es un{' '}
              <span className="gold-text" data-id="element-473">
                Sistema que hace que los clientes lleguen a tu puerta. Todos los
                días.
              </span>
            </h2>
            <p className="text-xl text-gray-400" data-id="element-474">
              Diseñado y construido específicamente para tu negocio.
            </p>
          </motion.div>

          <motion.div variants={staggerItem} className="text-center mb-16" data-id="element-475">
            <p className="text-2xl md:text-3xl font-bold mb-2" data-id="element-476">Presentamos:</p>
            <h3 className="text-4xl md:text-6xl gold-text text-shadow-gold font-black mb-4" data-id="element-477">
              El Sistema A.C.E.™
            </h3>
            <p className="text-xl text-gray-400" data-id="element-478">
              (Atracción. Conversión. Escala.)
            </p>
            <p className="text-lg text-gray-500 mt-4 max-w-3xl mx-auto italic" style={{
            textAlign: 'justify'
          }} data-id="element-479">
              El primer sistema de adquisición de clientes construido
              específicamente para dueños de negocios locales latinos en Estados
              Unidos.
            </p>
          </motion.div>

          {/* Phases */}
          <div className="space-y-6 mb-12" data-id="element-480">
            {phases.map((phase, index) => {
            const PhaseIcon = phase.icon;
            return <motion.div key={index} variants={staggerItem} className="bg-[#070707] border border-gray-800 rounded-xl p-8 transition-all duration-300 hover:border-[#10B981] hover:bg-[#059669]/[0.06] hover:shadow-[0_0_16px_rgba(5,150,105,0.2)]" data-id="element-481">
                  <div className="flex flex-col md:flex-row gap-6" data-id="element-482">
                    <div className="flex-shrink-0" data-id="element-483">
                      <div className="w-16 h-16 rounded-full gold-gradient flex items-center justify-center" data-id="element-484">
                        <PhaseIcon className="w-8 h-8 text-black" data-id="element-485" />
                      </div>
                    </div>
                    <div data-id="element-486">
                      <h4 className="text-xl md:text-2xl font-bold gold-text mb-2" data-id="element-487">
                        {phase.title}
                      </h4>
                      <p className="text-lg text-white mb-4" data-id="element-488">
                        {phase.subtitle}
                      </p>
                      <p className="text-gray-400" style={{
                    textAlign: 'justify'
                  }} data-id="element-489">
                        {phase.description}
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>

          {/* Industries */}
          <motion.div variants={staggerItem} className="mb-12" data-id="element-490">
            <h4 className="text-2xl text-center mb-6" data-id="element-491">
              El Sistema DOCTOR A.C.E.™ ha sido implementado en:
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 max-w-4xl mx-auto" data-id="element-492">
              {industries.map((industry, index) => {
              const industryLinks: Record<string, string> = {
                Roofing: 'https://roofing.thedoctorecommerce.com/',
                Landscaping: 'https://landscaping.thedoctorecommerce.com/',
                Moving: 'https://moving.thedoctorecommerce.com/',
                'Restaurantes y Food Trucks': 'https://negociosdecomida.thedoctorecommerce.com/',
                'Bakeries y Catering': 'https://negociosdecomida.thedoctorecommerce.com/',
                'Salones y Barberías': 'https://beauty.thedoctorecommerce.com/',
                'Spa y Estética': 'https://medspa.thedoctorecommerce.com/',
                'Beauty Services': 'https://beauty.thedoctorecommerce.com/',
                'Médicos y Cirujanos': 'https://healthcarepractices.thedoctorecommerce.com/',
                Dentistas: 'https://healthcarepractices.thedoctorecommerce.com/',
                'Real Estate': 'https://realestate.thedoctorecommerce.com/',
                Limpieza: 'https://cleaningservices.thedoctorecommerce.com/',
                Abogados: 'https://attorneys.thedoctorecommerce.com/',
                HVAC: 'https://hvac.thedoctorecommerce.com/',
                Plumbing: 'https://plumbing.thedoctorecommerce.com/'
              };
              return industryLinks[industry] ? <a key={index} href={industryLinks[industry]} target="_blank" rel="noopener noreferrer" className="px-3 py-2.5 bg-[#070707] border border-gray-800 rounded-lg text-sm text-gray-300 text-center cursor-pointer transition-all duration-300 hover:border-[#10B981] hover:bg-[#059669]/[0.08] hover:text-white hover:shadow-[0_0_12px_rgba(5,150,105,0.25)]" data-id="element-493">
                    {industry}
                  </a> : <span key={index} className="px-3 py-2.5 bg-[#070707] border border-gray-800 rounded-lg text-sm text-gray-300 text-center cursor-default transition-all duration-300 hover:border-[#10B981] hover:bg-[#059669]/[0.08] hover:text-white hover:shadow-[0_0_12px_rgba(5,150,105,0.25)]" data-id="element-494">
                    {industry}
                  </span>;
            })}
            </div>
          </motion.div>

          {/* Differentiators */}
          <motion.div variants={staggerItem} className="mb-12" data-id="element-495">
            <h4 className="text-2xl text-center mb-6" data-id="element-496">
              Lo que hace diferente al Sistema A.C.E.™:
            </h4>
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-497">
              {differentiators.map((item, index) => <div key={index} className="flex items-center gap-3" data-id="element-498">
                  <CheckIcon className="w-5 h-5 text-[#059669] flex-shrink-0" data-id="element-499" />
                  <span className="text-gray-300" data-id="element-500">{item}</span>
                </div>)}
            </div>
          </motion.div>

          <motion.div variants={staggerItem} className="text-center" data-id="element-501">
            <p className="text-2xl md:text-3xl font-bold mb-8" data-id="element-502">
              Un solo objetivo. Todos los días.
            </p>
            <p className="text-xl text-gray-300 mb-8 italic" data-id="element-503">
              "Que cuando abras tu negocio mañana… ya tengas clientes tocando tu
              puerta, listos para contratarte."
            </p>
            <CTABlock data-id="element-504" />
          </motion.div>
        </motion.div>
      </BackgroundSection>
    </>;
}
function IdentitySection() {
  return <>
      <SectionDivider data-id="element-505" />
      <SectionWrapper className="bg-[#111]" data-id="element-506">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-507">
          <motion.h2 variants={staggerItem} className="text-3xl md:text-5xl text-center mb-8" data-id="element-508">
            Tú no estás fallando.{' '}
            <span className="gold-text" data-id="element-509">Te están vendiendo lo equivocado.</span>
          </motion.h2>

          <motion.div variants={staggerItem} className="max-w-3xl mx-auto" data-id="element-510">
            <p className="text-xl text-gray-300 mb-6" style={{
            textAlign: 'justify'
          }} data-id="element-511">
              <span className="text-white font-semibold" data-id="element-512">
                Tú sí trabajas. Tú sí abres temprano. Tú sí contestas el
                teléfono.
              </span>
            </p>
            <p className="text-lg text-gray-400 mb-8" style={{
            textAlign: 'justify'
          }} data-id="element-513">
              Pero los que te venden marketing, muchas veces:
            </p>
            <ul className="text-left max-w-xl space-y-3 mb-8" data-id="element-514">
              <li className="flex items-center gap-3 text-gray-400" data-id="element-515">
                <RejectIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-516" />
                <span data-id="element-517">Nunca han vendido un servicio local de verdad</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400" data-id="element-518">
                <RejectIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-519" />
                <span data-id="element-520">No entienden tu comunidad</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400" data-id="element-521">
                <RejectIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-522" />
                <span data-id="element-523">No entienden que sin clientes no hay negocio</span>
              </li>
            </ul>
            <p className="text-2xl gold-text font-bold text-center" data-id="element-524">
              Nosotros hablamos tu idioma y jugamos este juego para ganar.
            </p>
          </motion.div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function ResultsSection() {
  const testimonials = [{
    name: 'Carlos M.',
    business: 'Roofing',
    location: 'Houston, TX',
    stars: 5,
    before: 'Llevaba 4 meses pagándole a una agencia $1,200/mes. Me mandaban reportes bonitos pero el teléfono no sonaba. Dos llamadas por semana si tenía suerte.',
    result: 'En la segunda semana con Doctor Ecommerce ya tenía 8 llamadas diarias. Tuve que contratar un segundo crew.',
    highlight: 'De 2 llamadas/semana → 8 llamadas/día'
  }, {
    name: 'María L.',
    business: 'Cleaning Services',
    location: 'Tampa, FL',
    stars: 5,
    before: 'Contraté a un "community manager" que me cobraba $800 al mes. Solo publicaba contenido en redes, pero nunca sonó el teléfono. Después intenté yo sola con Facebook Ads y gasté $600 sin una sola llamada.',
    result: 'Doctor Ecommerce me armó todo el sistema. Ahora cada lead me cuesta $12 y tengo agenda llena cada semana.',
    highlight: 'Costo por lead: de $45 → $12'
  }, {
    name: 'Ana R.',
    business: 'Salón de Belleza',
    location: 'Los Angeles, CA',
    stars: 5,
    before: 'Canjeaba servicios con influencers a cambio de "exposición". Muchos likes, cero citas. Estaba a punto de cerrar porque no podía cubrir la renta del local.',
    result: 'A las 3 semanas de implementar el sistema, tenía la agenda llena por 3 semanas seguidas. Ahora tengo lista de espera.',
    highlight: 'Agenda llena por 3 semanas consecutivas'
  }, {
    name: 'Jorge D.',
    business: 'Moving Company',
    location: 'Newark, NJ',
    stars: 5,
    before: 'Un gurú me vendió un curso de $2,000 para "escalar mi negocio". Hice todo lo que dijo — reels, contenido, hashtags. Resultado: 0 mudanzas nuevas en 2 meses.',
    result: 'Con el Sistema A.C.E.™ conseguí 47 reservas en 14 días. Tuve que rechazar trabajos porque no daba abasto.',
    highlight: '47 reservas en solo 14 días'
  }];
  return <>
      <SectionDivider data-id="element-525" />
      <SectionWrapper id="casos" className="bg-[#0A0A0A]" data-id="element-526">
        {/* ECG Background — Results */}
        <EcgBackground intensity="subtle" position="center" speed={10} data-id="element-527" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-528">
          <motion.div variants={staggerItem} className="text-center mb-4" data-id="element-529">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-530">
              <StarIcon className="w-3.5 h-3.5" data-id="element-531" />
              Casos reales
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-3xl md:text-5xl text-center mb-4" data-id="element-532">
            Resultados reales.{' '}
            <span className="gold-text" data-id="element-533">Historias reales.</span>
          </motion.h2>
          <motion.p variants={staggerItem} className="text-lg text-gray-400 text-center mb-14 max-w-2xl mx-auto" data-id="element-534">
            Estos negocios estaban exactamente donde tú estás hoy. Así les fue
            después de implementar el Sistema A.C.E.™
          </motion.p>

          {/* Proof Carousel */}
          <motion.div variants={staggerItem} className="mb-12" data-id="element-535">
            <ProofCarousel data-id="element-536" />
          </motion.div>

          {/* Testimonial Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-537">
            {testimonials.map((t, index) => <motion.div key={index} variants={staggerItem} className="relative bg-[#070707] border border-gray-800 rounded-xl overflow-hidden" data-id="element-538">
                <div className="p-6 md:p-8" data-id="element-539">
                  {/* Header: Stars + Industry badge */}
                  <div className="flex items-center justify-between mb-5" data-id="element-540">
                    <div className="flex items-center gap-1" data-id="element-541">
                      {Array.from({
                    length: t.stars
                  }).map((_, i) => <StarIcon key={i} className="w-4 h-4 text-[#D4A843] fill-[#D4A843]" data-id="element-542" />)}
                    </div>
                    <span className="text-xs text-gray-500 bg-[#1A1A1A] px-3 py-1 rounded-full border border-gray-800" data-id="element-543">
                      {t.business}
                    </span>
                  </div>

                  {/* Quote — Before story */}
                  <div className="relative mb-5" data-id="element-544">
                    <span className="absolute -top-2 -left-1 text-[#D4A843]/15 text-4xl font-serif leading-none select-none" data-id="element-545">
                      "
                    </span>
                    <p className="text-gray-400 text-sm leading-relaxed pl-5" style={{
                  textAlign: 'justify'
                }} data-id="element-546">
                      {t.before}
                    </p>
                  </div>

                  {/* Result — highlighted */}
                  <div className="bg-[#059669]/[0.06] border border-[#059669]/20 rounded-lg p-4 mb-5" data-id="element-547">
                    <p className="text-gray-300 text-sm leading-relaxed mb-2" style={{
                  textAlign: 'justify'
                }} data-id="element-548">
                      {t.result}
                    </p>
                    <div className="flex items-center gap-2" data-id="element-549">
                      <TrendingUpIcon className="w-4 h-4 text-[#059669] flex-shrink-0" data-id="element-550" />
                      <span className="text-[#059669] font-bold text-sm md:text-base" data-id="element-551">
                        {t.highlight}
                      </span>
                    </div>
                  </div>

                  {/* Author */}
                  <div className="flex items-center gap-3" data-id="element-552">
                    <div className="w-10 h-10 rounded-full gold-gradient flex items-center justify-center flex-shrink-0" data-id="element-553">
                      <span className="text-black font-bold text-sm" data-id="element-554">
                        {t.name.charAt(0)}
                      </span>
                    </div>
                    <div data-id="element-555">
                      <p className="text-white font-semibold text-sm" data-id="element-556">
                        {t.name}
                      </p>
                      <p className="text-gray-500 text-xs" data-id="element-557">
                        {t.business} — {t.location}
                      </p>
                    </div>
                    <div className="ml-auto" data-id="element-558">
                      <CheckIcon className="w-4 h-4 text-[#D4A843]/60" data-id="element-559" />
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <motion.p variants={staggerItem} className="text-sm text-gray-500 text-center mb-8 italic" data-id="element-560">
            *Los resultados varían según zona, oferta, demanda y capacidad de
            respuesta. Prometemos un sistema y ejecución real con resultados
            reales.
          </motion.p>

          <motion.div variants={staggerItem} className="flex justify-center" data-id="element-561">
            <CTAButton data-id="element-562">QUIERO RESULTADOS ASÍ</CTAButton>
          </motion.div>
          <div className="flex justify-center mt-3" data-id="element-563">
            <a href="https://calendar.app.google/PYPdcRoYne7QrNJA7" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#D4A843]/70 hover:text-[#D4A843] text-sm transition-colors" data-id="element-564">
              <CalendarIcon className="w-3.5 h-3.5" data-id="element-565" />
              <span data-id="element-566">Reservar una cita para mi diagnóstico gratis</span>
            </a>
          </div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function MediaSection() {
  const logos = [{
    src: 'https://cdn.magicpatterns.com/uploads/hzmUif78hbS4HfjFV6ucYy/IMG_7367.jpg',
    alt: 'Forbes'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/wykj9686A5gfVjv2YopME8/IMG_7395.webp',
    alt: 'Business Insider'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/gV6JpbvYezh1thFDnU3EGt/IMG_7396.webp',
    alt: 'Washington Post'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/oMxvF2mwHZz5yfZmpCwY9k/IMG_0206.png',
    alt: 'USA Today'
  }];
  // Duplicate logos for seamless infinite scroll
  const duplicatedLogos = [...logos, ...logos];
  return <>
      <SectionDivider data-id="element-567" />
      <SectionWrapper className="bg-[#111]" data-id="element-568">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-569">
          <motion.h2 variants={staggerItem} className="text-2xl md:text-4xl text-center mb-4" data-id="element-570">
            No somos "una agencia más".{' '}
            <span className="gold-text" data-id="element-571">Tenemos presencia real.</span>
          </motion.h2>
          <motion.p variants={staggerItem} className="text-gray-400 text-center mb-12" data-id="element-572">
            Presentados en:
          </motion.p>

          {/* Marquee carousel */}
          <motion.div variants={staggerItem} className="relative" data-id="element-573">
            {/* Keyframes */}
            <style data-id="element-574">{`
              @keyframes marquee-scroll {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
            `}</style>

            {/* Edge fade gradients */}
            <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-[#111] to-transparent z-10 pointer-events-none" data-id="element-575" />
            <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-[#111] to-transparent z-10 pointer-events-none" data-id="element-576" />

            {/* Scrolling track */}
            <div className="overflow-hidden" data-id="element-577">
              <div className="flex items-center gap-12 md:gap-16 w-max" style={{
              animation: 'marquee-scroll 20s linear infinite'
            }} data-id="element-578">
                {duplicatedLogos.map((logo, index) => <div key={index} className="flex-shrink-0 bg-white rounded-xl p-5 md:p-6 h-20 md:h-28 flex items-center justify-center" data-id="element-579">
                    <img src={logo.src} alt={logo.alt} className="h-full w-auto object-contain max-w-[180px] md:max-w-[220px]" data-id="element-580" />
                  </div>)}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function AntiGuruSection() {
  const guruLies = ['"Haz 3 reels y te lloverán clientes"', '"Tu energía atrae dinero"', '"Copia mi embudo millonario"', '"Automatiza todo y vive en la playa"'];
  const realTools = ['Oferta clara', 'Anuncios de respuesta directa', 'Landing que convierte', 'Seguimiento agresivo (pero profesional)', 'Medición y optimización'];
  return <>
      <SectionDivider data-id="element-581" />
      <SectionWrapper className="bg-[#0A0A0A]" data-id="element-582">
        {/* ECG Background — Anti-Guru */}
        <EcgBackground intensity="subtle" position="top" speed={9} data-id="element-583" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-584">
          {/* ═══ HEADLINE — Dramatic split with size contrast ═══ */}
          <motion.div variants={staggerItem} className="text-center mb-16 md:mb-20" data-id="element-585">
            <h2 className="text-2xl sm:text-3xl md:text-4xl text-gray-400 font-medium leading-snug mb-3" data-id="element-586">
              Si alguien te prometió riqueza "de la noche a la mañana",
            </h2>
            <p className="text-3xl sm:text-4xl md:text-6xl font-black leading-[1.05] gold-text" data-id="element-587">
              te vendió dopamina… no ventas.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto" data-id="element-588">
            {/* ═══ GURU LIES — Individual glass cards in a grid ═══ */}
            <motion.div variants={staggerItem} className="mb-14 md:mb-16" data-id="element-589">
              <p className="text-lg md:text-xl text-gray-300 mb-8" data-id="element-590">
                Los gurús te venden lo que se siente bien escuchar:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-591">
                {guruLies.map((lie, index) => <motion.div key={index} variants={staggerItem} className="relative group" data-id="element-592">
                    <div className="relative bg-[#070707] rounded-2xl p-5 md:p-6 border border-gray-800 overflow-hidden" data-id="element-593">
                      <div className="relative flex items-center gap-4" data-id="element-594">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center" data-id="element-595">
                          <RejectIcon className="w-4 h-4 text-red-400" data-id="element-596" />
                        </div>
                        <span className="text-gray-300 text-sm md:text-base leading-relaxed" data-id="element-597">
                          {lie}
                        </span>
                      </div>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* ═══ REALITY BLOCK — Premium glassmorphism with depth ═══ */}
            <motion.div variants={staggerItem} className="mb-16 md:mb-20" data-id="element-598">
              <div className="relative rounded-3xl overflow-hidden" data-id="element-599">
                {/* Ambient glow behind */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[60%] bg-[#D4A843] opacity-[0.04] blur-[100px] rounded-full pointer-events-none" data-id="element-600" />

                {/* Glass card */}
                <div className="relative bg-[#070707] rounded-3xl border border-gray-800 overflow-hidden" data-id="element-601">
                  <div className="p-8 md:p-12 lg:p-14" data-id="element-602">
                    {/* Statement */}
                    <div className="mb-10" data-id="element-603">
                      <p className="text-xl md:text-2xl text-white leading-relaxed" style={{
                      textAlign: 'justify'
                    }} data-id="element-604">
                        <span className="gold-text font-bold" data-id="element-605">
                          Pero tú no necesitas motivación.
                        </span>{' '}
                        Necesitas citas. Necesitas llamadas. Necesitas ventas
                        esta semana.
                      </p>
                    </div>

                    {/* What you need */}
                    <div data-id="element-606">
                      <p className="text-gray-400 text-sm uppercase tracking-[0.2em] font-semibold mb-6" data-id="element-607">
                        Y eso se construye con:
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-608">
                        {realTools.map((tool, index) => <div key={index} className="flex items-center gap-3 bg-[#0A0A0A] rounded-xl px-5 py-4 border border-gray-800 transition-all duration-300 hover:border-[#10B981] hover:bg-[#059669]/[0.08] hover:shadow-[0_0_12px_rgba(5,150,105,0.25)]" data-id="element-609">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#059669]/10 flex items-center justify-center" data-id="element-610">
                              <CheckIcon className="w-3.5 h-3.5 text-[#059669]" data-id="element-611" />
                            </div>
                            <span className="text-gray-200 text-sm font-medium" data-id="element-612">
                              {tool}
                            </span>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* ═══ CLOSING — Oversized impact statement ═══ */}
            <motion.div variants={staggerItem} className="text-center" data-id="element-613">
              <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight" data-id="element-614">
                <span className="text-white" data-id="element-615">Aquí no vienes a soñar.</span>
                <br data-id="element-616" />
                <span className="gold-text" data-id="element-617">Vienes a cobrar.</span>
              </p>
            </motion.div>
          </div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function ServicesSection() {
  const serviceCategories = [{
    icon: BarChart3Icon,
    title: 'ADQUISICIÓN DE CLIENTES — PUBLICIDAD DE ALTO RENDIMIENTO',
    services: ['Anuncios diseñados para generar clientes reales, no likes ni comentarios', 'Capturamos a la persona que ya está buscando tu servicio en este momento', 'Perseguimos a quien ya te vio pero no actuó — hasta que actúe', 'Acceso a cuentas publicitarias de alto nivel con menor riesgo de restricciones']
  }, {
    icon: ShieldCheckIcon,
    title: 'BLINDAJE DIGITAL — PROTECCIÓN Y ESTABILIDAD',
    services: ['Estructura anti-baneos: configuración profesional para reducir riesgo de suspensiones', 'Perfiles de Ads optimizados: Business Manager, Pixel y dominios configurados correctamente', 'Respaldo y recuperación de cuentas: perder una cuenta no puede significar perder tu negocio']
  }, {
    icon: GlobeIcon,
    title: 'PRESENCIA DIGITAL CON AUTORIDAD',
    services: ['Páginas web de alto impacto: diseñadas para generar confianza y convertir', 'SEO local: que te encuentren cuando buscan tu servicio en tu ciudad', 'Landing pages de conversión: páginas específicas para cada campaña']
  }, {
    icon: StarIcon,
    title: 'REPUTACIÓN Y PRUEBA SOCIAL',
    services: ['Estrategia para aparecer primero con autoridad cuando buscan tu servicio', 'Sistema para conseguir reseñas de clientes reales de forma consistente', 'Manejo de reputación online: controlamos la narrativa de tu negocio', 'Construcción de prueba social: testimonios y casos de éxito']
  }, {
    icon: ZapIcon,
    title: 'AUTOMATIZACIÓN Y SEGUIMIENTO',
    services: ['Seguimiento automatizado: WhatsApp, SMS y email que responden sin que hagas nada', 'Reactivación de leads dormidos: los que dijeron "después" — los volvemos a traer', 'Optimización semanal de campañas: revisamos, ajustamos y escalamos cada semana']
  }];
  // Custom crosshair/precision icon for diagnosis
  const DiagnosisIcon = () => <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" data-id="element-618">
      <circle cx="16" cy="16" r="11" stroke="currentColor" strokeWidth="1.5" data-id="element-619" />
      <circle cx="16" cy="16" r="6" stroke="currentColor" strokeWidth="1.5" data-id="element-620" />
      <circle cx="16" cy="16" r="2" fill="currentColor" data-id="element-621" />
      <line x1="16" y1="1" x2="16" y2="7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-622" />
      <line x1="16" y1="25" x2="16" y2="31" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-623" />
      <line x1="1" y1="16" x2="7" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-624" />
      <line x1="25" y1="16" x2="31" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-625" />
    </svg>;
  return <>
      <SectionDivider data-id="element-626" />
      <BackgroundSection overlay="bg-[#111]/95" data-id="element-627">
        {/* ECG Background — Services */}
        <EcgBackground intensity="subtle" position="bottom" speed={10} bgColor="#111" data-id="element-628" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-629">
          {/* Section Header */}
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-630">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-631">
              <ZapIcon className="w-3.5 h-3.5" data-id="element-632" />
              Ecosistema completo
            </span>
          </motion.div>

          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-633">
            <p className="text-[#D4A843] text-base md:text-lg mb-4 tracking-wide" data-id="element-634">
              ¿Qué incluye trabajar con nosotros?
            </p>
            <h2 className="text-3xl md:text-5xl mb-4 leading-tight" data-id="element-635">
              No te vendemos un servicio.{' '}
              <span className="gold-text" data-id="element-636">
                Te integramos un ecosistema completo.
              </span>
            </h2>
          </motion.div>

          {/* Decorative separator */}
          <motion.div variants={staggerItem} className="flex items-center justify-center gap-4 mb-16 md:mb-20" data-id="element-637">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-[#D4A843]/40" data-id="element-638" />
            <div className="w-1.5 h-1.5 rotate-45 bg-[#D4A843]/30" data-id="element-639" />
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-[#D4A843]/40" data-id="element-640" />
          </motion.div>

          {/* ═══ PASO 1 — Free Diagnosis ═══ */}
          <motion.div variants={staggerItem} className="mb-16" data-id="element-641">
            {/* Step indicator */}
            <div className="flex items-center gap-4 mb-8" data-id="element-642">
              <div className="flex items-center gap-3" data-id="element-643">
                <div className="w-10 h-10 rounded-full border-2 border-[#D4A843] flex items-center justify-center" data-id="element-644">
                  <span className="text-[#D4A843] font-bold text-sm" data-id="element-645">01</span>
                </div>
                <div className="h-px w-8 bg-gradient-to-r from-[#D4A843]/60 to-transparent" data-id="element-646" />
              </div>
              <p className="text-sm text-gray-400 font-semibold tracking-[0.15em] uppercase" data-id="element-647">
                Antes de cobrar un solo dólar
              </p>
            </div>

            {/* Diagnosis Card — Premium */}
            <div className="relative" data-id="element-648">
              <div className="relative bg-[#070707] border border-gray-800 rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#10B981] hover:bg-[#059669]/[0.04] hover:shadow-[0_0_24px_rgba(5,150,105,0.15)] group/diag" data-id="element-649">
                <div className="relative p-8 md:p-12" data-id="element-650">
                  {/* Header with icon */}
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-8" data-id="element-651">
                    <div className="flex-shrink-0 w-16 h-16 rounded-xl gold-gradient flex items-center justify-center" data-id="element-652">
                      <DiagnosisIcon data-id="element-653" />
                    </div>
                    <div className="text-center md:text-left" data-id="element-654">
                      <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 leading-tight" data-id="element-655">
                        Diagnóstico de Adquisición de Clientes
                      </h3>
                      <div className="flex items-center justify-center md:justify-start gap-3 mt-3" data-id="element-656">
                        <div className="h-px w-8 bg-[#D4A843]/40" data-id="element-657" />
                        <p className="text-xl md:text-2xl gold-text font-black tracking-wide" data-id="element-658">
                          COMPLETAMENTE GRATIS
                        </p>
                        <div className="h-px w-8 bg-[#D4A843]/40" data-id="element-659" />
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent mb-8" data-id="element-660" />

                  {/* Body */}
                  <div className="max-w-3xl" data-id="element-661">
                    <p className="text-gray-300 italic mb-8 text-base leading-relaxed" style={{
                    textAlign: 'justify'
                  }} data-id="element-662">
                      Esto no es una llamada de ventas disfrazada de
                      consultoría.
                    </p>

                    <div className="grid sm:grid-cols-2 gap-4 mb-8" data-id="element-663">
                      {['Por qué no te están llegando clientes y qué haremos para cambiarlo', 'Qué está frenando el crecimiento de tu negocio específicamente', 'Cuál es la oportunidad más grande que tienes ahora mismo', 'Plan paso a paso para ver cambios en los siguientes 7 días'].map((item, i) => <div key={i} className="flex items-start gap-3 bg-[#0A0A0A] rounded-lg p-4 border border-gray-800 transition-all duration-300 group-hover/diag:border-[#10B981]/40 group-hover/diag:bg-[#059669]/[0.06] group-hover/diag:shadow-[0_0_12px_rgba(5,150,105,0.15)]" data-id="element-664">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#059669]/10 border border-[#059669]/30 flex items-center justify-center mt-0.5" data-id="element-665">
                            <CheckIcon className="w-3.5 h-3.5 text-[#059669]" data-id="element-666" />
                          </div>
                          <span className="text-gray-300 text-sm leading-relaxed" data-id="element-667">
                            {item}
                          </span>
                        </div>)}
                    </div>

                    <p className="text-gray-400 italic text-sm leading-relaxed" style={{
                    textAlign: 'justify'
                  }} data-id="element-668">
                      Y te lo decimos todo. Aunque no trabajes con nosotros.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ═══ PASO 2 — Services ═══ */}
          <motion.div variants={staggerItem} className="mb-16" data-id="element-669">
            {/* Step indicator */}
            <div className="flex items-center gap-4 mb-10" data-id="element-670">
              <div className="flex items-center gap-3" data-id="element-671">
                <div className="w-10 h-10 rounded-full border-2 border-[#D4A843] flex items-center justify-center" data-id="element-672">
                  <span className="text-[#D4A843] font-bold text-sm" data-id="element-673">02</span>
                </div>
                <div className="h-px w-8 bg-gradient-to-r from-[#D4A843]/60 to-transparent" data-id="element-674" />
              </div>
              <p className="text-sm text-gray-400 font-semibold tracking-[0.15em] uppercase" data-id="element-675">
                Si decides avanzar
              </p>
            </div>

            {/* Service Categories — Editorial Layout */}
            <div className="space-y-0" data-id="element-676">
              {serviceCategories.map((category, index) => {
              const CategoryIcon = category.icon;
              return <motion.div key={index} variants={staggerItem} className="group" data-id="element-677">
                    {/* Top divider for first item */}
                    {index === 0 && <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent" data-id="element-678" />}

                    <div className="py-8 md:py-10" data-id="element-679">
                      <div className="flex gap-5 md:gap-8" data-id="element-680">
                        {/* Left column: number + icon */}
                        <div className="flex-shrink-0 flex flex-col items-center gap-3" data-id="element-681">
                          <span className="text-[#D4A843]/15 font-black text-3xl md:text-4xl leading-none select-none" data-id="element-682">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <div className="w-11 h-11 rounded-lg bg-[#D4A843]/10 border border-[#D4A843]/20 flex items-center justify-center group-hover:bg-[#D4A843]/15 group-hover:border-[#D4A843]/40 transition-all duration-300" data-id="element-683">
                            <CategoryIcon className="w-5 h-5 text-[#D4A843]" data-id="element-684" />
                          </div>
                        </div>

                        {/* Right column: content */}
                        <div className="flex-1 min-w-0" data-id="element-685">
                          <h4 className="text-base md:text-lg font-bold text-white mb-4 leading-tight group-hover:text-[#D4A843] transition-colors duration-300" data-id="element-686">
                            {category.title}
                          </h4>
                          <ul className="space-y-2.5" data-id="element-687">
                            {category.services.map((service, sIndex) => <li key={sIndex} className="flex items-start gap-3 text-gray-400 text-sm" data-id="element-688">
                                <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#059669] mt-2" data-id="element-689" />
                                <span className="leading-relaxed" data-id="element-690">
                                  {service}
                                </span>
                              </li>)}
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Bottom divider */}
                    <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent" data-id="element-691" />
                  </motion.div>;
            })}
            </div>
          </motion.div>

          {/* Closing */}
          <motion.div variants={staggerItem} className="text-center" data-id="element-692">
            {/* Decorative separator */}
            <div className="flex items-center justify-center gap-4 mb-10" data-id="element-693">
              <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#D4A843]/40" data-id="element-694" />
              <div className="w-2 h-2 rotate-45 border border-[#D4A843]/40" data-id="element-695" />
              <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#D4A843]/40" data-id="element-696" />
            </div>

            <p className="text-xl text-gray-300 mb-8 text-center max-w-3xl mx-auto leading-relaxed" data-id="element-697">
              Mientras tú haces tu trabajo —{' '}
              <span className="gold-text font-semibold" data-id="element-698">
                nosotros construimos el sistema que hace que los clientes te
                encuentren y te llamen.
              </span>
            </p>
            <div className="flex justify-center" data-id="element-699">
              <CTABlock data-id="element-700" />
            </div>
          </motion.div>
        </motion.div>
      </BackgroundSection>
    </>;
}
function QualificationSection() {
  const forYou = ['Dueños de negocio que pueden atender más clientes', 'Que están listos para seguir un proceso', 'Que quieren resultados inmediatos, no "branding"'];
  const notForYou = ['Si quieres "hacerte viral" sin vender nada', 'Si no contestas llamadas ni mensajes', 'Si buscas magia con $1/día', 'Si quieres que el marketing funcione pero sin cambiar nada'];
  // Custom premium approve icon — shield with checkmark
  const ApproveShieldIcon = ({
    className = ''
  }: {
    className?: string;
  }) => <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-701">
      <path d="M14 2L4 7v6.5c0 6.5 4.3 12.6 10 14 5.7-1.4 10-7.5 10-14V7L14 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="currentColor" fillOpacity="0.06" data-id="element-702" />
      <path d="M9.5 14.5l3 3 6-6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-703" />
    </svg>;
  // Custom premium deny icon — shield with X
  const DenyShieldIcon = ({
    className = ''
  }: {
    className?: string;
  }) => <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-704">
      <path d="M14 2L4 7v6.5c0 6.5 4.3 12.6 10 14 5.7-1.4 10-7.5 10-14V7L14 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="currentColor" fillOpacity="0.06" data-id="element-705" />
      <path d="M10.5 10.5l7 7M17.5 10.5l-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-706" />
    </svg>;
  // Custom bullet check — minimal elegant circle + check
  const BulletCheck = ({
    className = ''
  }: {
    className?: string;
  }) => <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-707">
      <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.2" strokeOpacity="0.3" data-id="element-708" />
      <path d="M5.5 9.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-709" />
    </svg>;
  // Custom bullet X — minimal elegant circle + X
  const BulletX = ({
    className = ''
  }: {
    className?: string;
  }) => <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} data-id="element-710">
      <circle cx="9" cy="9" r="8" stroke="currentColor" strokeWidth="1.2" strokeOpacity="0.3" data-id="element-711" />
      <path d="M6.5 6.5l5 5M11.5 6.5l-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-712" />
    </svg>;
  return <>
      <SectionDivider data-id="element-713" />
      <SectionWrapper className="bg-[#0A0A0A]" data-id="element-714">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-715">
          {/* Section Badge */}
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-716">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-717">
              <TargetIcon className="w-3.5 h-3.5" data-id="element-718" />
              Calificación
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-3xl md:text-5xl text-center mb-4 leading-tight" data-id="element-719">
            ¿Es esto <span className="gold-text" data-id="element-720">para ti</span>?
          </motion.h2>

          <motion.p variants={staggerItem} className="text-gray-500 text-center text-sm md:text-base mb-14 max-w-xl mx-auto" data-id="element-721">
            No trabajamos con todos. Trabajamos con quienes están listos para
            crecer.
          </motion.p>

          {/* Decorative separator */}
          <motion.div variants={staggerItem} className="flex items-center justify-center gap-4 mb-14" data-id="element-722">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-[#D4A843]/40" data-id="element-723" />
            <div className="w-1.5 h-1.5 rotate-45 bg-[#D4A843]/30" data-id="element-724" />
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-[#D4A843]/40" data-id="element-725" />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto" data-id="element-726">
            {/* ═══ PARA QUIÉN SÍ ═══ */}
            <motion.div variants={staggerItem} className="relative group" data-id="element-727">
              <div className="relative bg-[#070707] border border-gray-800 rounded-2xl p-8 md:p-10 overflow-hidden" data-id="element-728">
                {/* Header */}
                <div className="relative flex items-center gap-4 mb-8" data-id="element-729">
                  <div className="w-12 h-12 rounded-xl bg-[#D4A843]/10 border border-[#D4A843]/25 flex items-center justify-center" data-id="element-730">
                    <ApproveShieldIcon className="w-7 h-7 text-[#D4A843]" data-id="element-731" />
                  </div>
                  <div data-id="element-732">
                    <p className="text-xs text-[#D4A843]/60 font-semibold tracking-[0.2em] uppercase mb-1" data-id="element-733">
                      Ideal para
                    </p>
                    <h3 className="text-xl md:text-2xl font-bold text-white leading-tight" data-id="element-734">
                      Para quién SÍ
                    </h3>
                  </div>
                </div>

                {/* Divider */}
                <div className="h-px bg-gradient-to-r from-[#D4A843]/20 via-[#D4A843]/10 to-transparent mb-7" data-id="element-735" />

                {/* List */}
                <ul className="relative space-y-5" data-id="element-736">
                  {forYou.map((item, index) => <li key={index} className="flex items-start gap-3.5" data-id="element-737">
                      <div className="flex-shrink-0 mt-0.5" data-id="element-738">
                        <BulletCheck className="w-5 h-5 text-[#D4A843]" data-id="element-739" />
                      </div>
                      <span className="text-gray-300 text-sm md:text-base leading-relaxed" data-id="element-740">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </motion.div>

            {/* ═══ PARA QUIÉN NO ═══ */}
            <motion.div variants={staggerItem} className="relative group" data-id="element-741">
              <div className="relative bg-[#070707] border border-gray-800 rounded-2xl p-8 md:p-10 overflow-hidden" data-id="element-742">
                {/* Header */}
                <div className="relative flex items-center gap-4 mb-8" data-id="element-743">
                  <div className="w-12 h-12 rounded-xl bg-white/[0.04] border border-gray-700/50 flex items-center justify-center" data-id="element-744">
                    <DenyShieldIcon className="w-7 h-7 text-gray-500" data-id="element-745" />
                  </div>
                  <div data-id="element-746">
                    <p className="text-xs text-gray-600 font-semibold tracking-[0.2em] uppercase mb-1" data-id="element-747">
                      No es para
                    </p>
                    <h3 className="text-xl md:text-2xl font-bold text-white leading-tight" data-id="element-748">
                      Para quién NO
                    </h3>
                  </div>
                </div>

                {/* Divider */}
                <div className="h-px bg-gradient-to-r from-gray-700/30 via-gray-800/20 to-transparent mb-7" data-id="element-749" />

                {/* List */}
                <ul className="relative space-y-5" data-id="element-750">
                  {notForYou.map((item, index) => <li key={index} className="flex items-start gap-3.5" data-id="element-751">
                      <div className="flex-shrink-0 mt-0.5" data-id="element-752">
                        <BulletX className="w-5 h-5 text-gray-600" data-id="element-753" />
                      </div>
                      <span className="text-gray-500 text-sm md:text-base leading-relaxed" data-id="element-754">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function GuaranteeSection() {
  const [showTerms, setShowTerms] = useState(false);
  return <>
      <SectionDivider data-id="element-755" />
      <BackgroundSection data-id="element-756">
        {/* ECG Background — Guarantee */}
        <EcgBackground intensity="subtle" position="top" speed={12} data-id="element-757" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-758">
          {/* Badge */}
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-759">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-760">
              <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-761" />
              Garantía
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-3xl md:text-5xl text-center mb-4 leading-tight" data-id="element-762">
            Nosotros ponemos el dinero{' '}
            <span className="gold-text" data-id="element-763">donde están las palabras.</span>
          </motion.h2>

          <motion.p variants={staggerItem} className="text-gray-500 text-center text-sm md:text-base mb-14 max-w-xl mx-auto" data-id="element-764">
            No te pedimos que confíes ciegamente. Te damos una garantía real.
          </motion.p>

          {/* Main Guarantee Card */}
          <motion.div variants={staggerItem} className="max-w-4xl mx-auto" data-id="element-765">
            <div className="relative rounded-3xl overflow-hidden" data-id="element-766">
              {/* Subtle ambient glow behind card */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] bg-[#D4A843] opacity-[0.04] blur-[100px] rounded-full pointer-events-none" data-id="element-767" />

              {/* Card */}
              <div className="relative bg-[#111] rounded-3xl border border-gray-800/60 overflow-hidden" data-id="element-768">
                {/* Top gold gradient strip */}
                <div className="h-1 gold-gradient" data-id="element-769" />

                <div className="p-8 md:p-12 lg:p-14" data-id="element-770">
                  {/* Hero stat row */}
                  <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 mb-10" data-id="element-771">
                    {/* Big number */}
                    <div className="flex-shrink-0 text-center md:text-left" data-id="element-772">
                      <div className="relative inline-block" data-id="element-773">
                        <span className="text-7xl md:text-8xl lg:text-9xl font-black gold-text leading-none tracking-tighter" data-id="element-774">
                          30
                        </span>
                        <span className="block text-lg md:text-xl font-bold text-white -mt-1 tracking-wide" data-id="element-775">
                          DÍAS
                        </span>
                      </div>
                    </div>

                    {/* Promise text */}
                    <div className="flex-1 text-center md:text-left" data-id="element-776">
                      <h3 className="text-2xl md:text-3xl font-black text-white mb-3 leading-tight" data-id="element-777">
                        LA GARANTÍA A.C.E.™
                      </h3>
                      <p className="text-gray-300 text-base md:text-lg leading-relaxed" style={{
                      textAlign: 'justify'
                    }} data-id="element-778">
                        Si en los primeros 30 días de trabajo juntos no ves un
                        incremento real y medible en la cantidad de leads que
                        llegan a tu negocio cada día, te devolvemos el{' '}
                        <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-[#D4A843]/10 border border-[#D4A843]/25 text-[#D4A843] font-bold text-sm align-middle" data-id="element-779">
                          100% de tu inversión
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="h-px bg-gray-800/80 mb-10" data-id="element-780" />

                  {/* What it covers — clean grid */}
                  <div className="mb-10" data-id="element-781">
                    <p className="text-sm text-gray-500 font-semibold tracking-[0.15em] uppercase mb-6" data-id="element-782">
                      ¿Qué cubre esta garantía?
                    </p>
                    <div className="grid sm:grid-cols-3 gap-4" data-id="element-783">
                      {[{
                      stat: 'Leads',
                      desc: 'Incremento medible y demostrable en el volumen de leads entrantes'
                    }, {
                      stat: 'Calificados',
                      desc: 'Leads dentro del nicho y zona geográfica acordados'
                    }, {
                      stat: '7 días',
                      desc: 'Implementación completa del sistema antes de finalizar la primera semana'
                    }].map((item, i) => <div key={i} className="bg-[#0A0A0A] rounded-2xl p-5 md:p-6" data-id="element-784">
                          <p className="text-[#D4A843] font-black text-xl md:text-2xl mb-2" data-id="element-785">
                            {item.stat}
                          </p>
                          <p className="text-gray-400 text-sm leading-relaxed" data-id="element-786">
                            {item.desc}
                          </p>
                        </div>)}
                    </div>
                  </div>

                  {/* Bottom quote */}
                  <div className="bg-[#0A0A0A] rounded-2xl p-6 md:p-8 mb-8" data-id="element-787">
                    <p className="text-gray-300 text-base md:text-lg leading-relaxed" style={{
                    textAlign: 'justify'
                  }} data-id="element-788">
                      <span className="font-bold text-white" data-id="element-789">
                        Así de simple. Así de directo.
                      </span>{' '}
                      Porque nosotros no vivimos de cobrarte meses sin
                      resultados. Vivimos de que tu negocio crezca — y de que lo
                      cuentes.
                    </p>
                  </div>

                  {/* Terms accordion — clean style */}
                  <div data-id="element-790">
                    <button onClick={() => setShowTerms(!showTerms)} className="flex items-center gap-3 text-gray-500 hover:text-gray-300 transition-colors group" data-id="element-791">
                      <motion.div animate={{
                      rotate: showTerms ? 180 : 0
                    }} transition={{
                      duration: 0.3,
                      ease: [0.4, 0, 0.2, 1]
                    }} data-id="element-792">
                        <ChevronDownIcon className="w-4 h-4" data-id="element-793" />
                      </motion.div>
                      <span className="text-sm font-medium" data-id="element-794">
                        Términos y condiciones de la garantía
                      </span>
                    </button>

                    <motion.div initial={false} animate={{
                    height: showTerms ? 'auto' : 0,
                    opacity: showTerms ? 1 : 0
                  }} transition={{
                    height: {
                      duration: 0.4,
                      ease: [0.4, 0, 0.2, 1]
                    },
                    opacity: {
                      duration: showTerms ? 0.35 : 0.2,
                      delay: showTerms ? 0.1 : 0
                    }
                  }} className="overflow-hidden" data-id="element-795">
                      <div className="pt-5 text-xs text-gray-500 space-y-2" data-id="element-796">
                        <p data-id="element-797">Esta garantía aplica siempre y cuando:</p>
                        <ul className="space-y-1.5 ml-4" data-id="element-798">
                          {['El presupuesto publicitario acordado esté disponible y activo desde el inicio', 'El cliente responda a tiempo las solicitudes de información, materiales o aprobaciones', 'La oferta del negocio sea real, legal y entregable', 'No haya habido interferencia, cambios unilaterales o cancelaciones de campañas activas', 'El período de 30 días se cuente desde la fecha de lanzamiento oficial de las campañas', 'El cliente haya atendido los leads recibidos'].map((term, i) => <li key={i} className="flex items-start gap-2" data-id="element-799">
                              <span className="text-gray-600 mt-0.5" data-id="element-800">•</span>
                              <span data-id="element-801">{term}</span>
                            </li>)}
                        </ul>
                        <p className="italic text-gray-600 pt-1" data-id="element-802">
                          Esta garantía no aplica si el cliente no cumple con
                          los acuerdos operativos establecidos al inicio del
                          proyecto.
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA below card */}
          <motion.div variants={staggerItem} className="text-center mt-12" data-id="element-803">
            <p className="text-lg md:text-xl text-gray-300 mb-6" data-id="element-804">
              Sin riesgo. Sin excusas. Sin humo.
            </p>
            <div className="flex justify-center" data-id="element-805">
              <CTABlock data-id="element-806" />
            </div>
            <p className="text-gray-600 text-sm mt-4 italic" data-id="element-807">
              Si en 30 días no ves más leads llegando a tu negocio, no pagas.
            </p>
          </motion.div>
        </motion.div>
      </BackgroundSection>
    </>;
}
function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [{
    question: 'Ya hice anuncios y no funcionó.',
    answer: 'Perfecto. Eso significa que ya pagaste la lección. La mayoría falla por 3 cosas: oferta, landing y seguimiento. El anuncio solo no salva un negocio. Nosotros arreglamos el sistema completo.'
  }, {
    question: 'Ya tuve agencia y me fue mal.',
    answer: 'Normal. Muchas agencias cobran por "gestionar", no por convertir. Reportes ≠ ventas. Aquí medimos llamadas, citas y cierres.'
  }, {
    question: '¿Necesito mucho presupuesto?',
    answer: 'Necesitas el presupuesto correcto, no "mucho". Si tu servicio deja buen margen, con que el costo por lead sea rentable, escala. Si tu margen es bajo, ajustamos oferta y upsells.'
  }, {
    question: '¿Y si mi ciudad está saturada?',
    answer: 'Saturado está el contenido, no el mercado. Si hay competencia, hay demanda. Ganar depende de oferta + velocidad + confianza + seguimiento.'
  }, {
    question: 'No tengo tiempo.',
    answer: 'Si hoy no tienes tiempo para conseguir clientes, mañana tendrás tiempo de sobra. Esto se hace rápido y con estructura.'
  }];
  return <>
      <SectionDivider data-id="element-808" />
      <SectionWrapper className="bg-[#0A0A0A]" data-id="element-809">
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-810">
          {/* Section Badge */}
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-811">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-812">
              <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-813" />
              Dudas
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-3xl md:text-5xl text-center mb-4 leading-tight" data-id="element-814">
            Preguntas <span className="gold-text" data-id="element-815">frecuentes</span>
          </motion.h2>

          <motion.p variants={staggerItem} className="text-gray-500 text-center text-sm md:text-base mb-14 max-w-lg mx-auto" data-id="element-816">
            Si todavía tienes dudas, probablemente la respuesta está aquí.
          </motion.p>

          <motion.div variants={staggerItem} className="max-w-3xl mx-auto space-y-3" data-id="element-817">
            {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return <div key={index} className={`relative rounded-2xl transition-all duration-300 ${isOpen ? 'bg-[#070707] border border-[#10B981] bg-[#059669]/[0.06] shadow-[0_0_16px_rgba(5,150,105,0.2)]' : 'bg-[#0A0A0A] border border-gray-800 hover:border-[#10B981] hover:bg-[#059669]/[0.06] hover:shadow-[0_0_16px_rgba(5,150,105,0.2)]'}`} data-id="element-818">
                  {/* Active left accent bar */}
                  <div className={`absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#D4A843] to-[#D4A843]/40 rounded-l-2xl transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`} data-id="element-819" />

                  <button onClick={() => setOpenIndex(isOpen ? null : index)} className="w-full px-6 md:px-8 py-6 text-left flex items-center gap-5 group cursor-pointer" data-id="element-820">
                    {/* Number */}
                    <span className={`flex-shrink-0 text-sm font-bold tabular-nums transition-colors duration-300 ${isOpen ? 'text-[#D4A843]' : 'text-gray-600'}`} data-id="element-821">
                      {String(index + 1).padStart(2, '0')}
                    </span>

                    {/* Question */}
                    <span className={`flex-1 text-base md:text-lg font-semibold leading-snug transition-colors duration-300 ${isOpen ? 'text-white' : 'text-gray-300 group-hover:text-white'}`} data-id="element-822">
                      {faq.question}
                    </span>

                    {/* Chevron — always gold tinted for visibility */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#D4A843]/15 border border-[#D4A843]/40' : 'bg-[#D4A843]/5 border border-[#D4A843]/20 group-hover:bg-[#D4A843]/10 group-hover:border-[#D4A843]/30'}`} data-id="element-823">
                      <ChevronDownIcon className={`w-4 h-4 text-[#D4A843] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-824" />
                    </div>
                  </button>

                  {/* Answer — simple conditional render for reliability */}
                  {isOpen && <div className="px-6 md:px-8 pb-6 pl-[4.25rem] md:pl-[4.75rem] animate-[fadeIn_0.3s_ease-out]" data-id="element-825">
                      <p className="text-gray-400 text-sm md:text-base leading-relaxed" data-id="element-826">
                        {faq.answer}
                      </p>
                    </div>}
                </div>;
          })}
          </motion.div>
        </motion.div>
      </SectionWrapper>
    </>;
}
function FormSection() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    city_state: '',
    business_type: '',
    business_name: '',
    instagram: '',
    avg_ticket: '',
    clients_per_week: '',
    new_clients_needed: '',
    ads_before: '',
    ads_experience: '',
    monthly_ad_spend_range: ''
  });
  const [sendStatus, setSendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendStatus('sending');
    try {
      const form = formRef.current;
      if (!form) throw new Error('Form not found');
      // Fill hidden tracking fields
      const pageUrlInput = form.querySelector<HTMLInputElement>('input[name="page_url"]');
      const dateInput = form.querySelector<HTMLInputElement>('input[name="date"]');
      if (pageUrlInput) pageUrlInput.value = window.location.href;
      if (dateInput) dateInput.value = new Date().toLocaleString();
      // 1) Email para ADMIN (notificación con datos del lead)
      await emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ADMIN, form, EMAILJS_PUBLIC_KEY);
      // 2) Email AUTO-REPLY para el CLIENTE (confirmación)
      await emailjs.sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_AUTOREPLY, form, EMAILJS_PUBLIC_KEY);
      setSendStatus('sent');
      // Meta Pixel: Track CompleteRegistration (form submitted with all data)
      if (window.fbq) window.fbq('track', 'CompleteRegistration');
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        city_state: '',
        business_type: '',
        business_name: '',
        instagram: '',
        avg_ticket: '',
        clients_per_week: '',
        new_clients_needed: '',
        ads_before: '',
        ads_experience: '',
        monthly_ad_spend_range: ''
      });
      setTimeout(() => setSendStatus('idle'), 4000);
    } catch (error) {
      console.error('EmailJS error:', error);
      setSendStatus('error');
      setTimeout(() => setSendStatus('idle'), 4000);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <>
      <SectionDivider data-id="element-827" />
      <BackgroundSection id="formulario" overlay="bg-[#111]/94" data-id="element-828">
        {/* ECG Background — Form CTA */}
        <EcgBackground intensity="medium" position="bottom" speed={9} bgColor="#111" data-id="element-829" />

        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} data-id="element-830">
          {/* Section Badge */}
          <motion.div variants={staggerItem} className="text-center mb-6" data-id="element-831">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4A843]/20 bg-[#D4A843]/5 text-[#D4A843] text-xs font-semibold tracking-[0.2em] uppercase" data-id="element-832">
              <CalendarIcon className="w-3.5 h-3.5" data-id="element-833" />
              Diagnóstico gratuito
            </span>
          </motion.div>

          <motion.h2 variants={staggerItem} className="text-center mb-3" data-id="element-834">
            <span className="block text-lg md:text-xl text-gray-400 mb-3 tracking-wide" data-id="element-835">
              Si estás cansado de intentar y fallar…
            </span>
            <span className="block text-4xl md:text-6xl font-black gold-text leading-[1.1]" data-id="element-836">
              Hoy se acaba.
            </span>
          </motion.h2>

          <motion.p variants={staggerItem} className="text-base md:text-lg text-gray-400 text-center mb-14 max-w-2xl mx-auto leading-relaxed" data-id="element-837">
            Agenda tu diagnóstico y te decimos exactamente qué está bloqueando
            tus clientes, qué cambiar esta semana y cómo debería verse tu embudo
            para producir diario.
          </motion.p>

          <motion.form ref={formRef} id="lead-form" variants={staggerItem} onSubmit={handleSubmit} className="max-w-2xl mx-auto bg-[#1A1A1A]/95 backdrop-blur-sm border border-[#D4A843]/30 rounded-xl p-8" data-id="element-838">
            {/* Hidden tracking fields */}
            <input type="hidden" name="page_url" data-id="element-839" />
            <input type="hidden" name="date" data-id="element-840" />

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-841">
              <div data-id="element-842">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-843">
                  Nombre *
                </label>
                <input type="text" name="full_name" required value={formData.full_name} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="Tu nombre" data-id="element-844" />
              </div>
              <div data-id="element-845">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-846">
                  Email *
                </label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="tu@email.com" data-id="element-847" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-848">
              <div data-id="element-849">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-850">
                  Teléfono *
                </label>
                <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="(123) 456-7890" data-id="element-851" />
              </div>
              <div data-id="element-852">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-853">
                  Ciudad/Estado *
                </label>
                <input type="text" name="city_state" required value={formData.city_state} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="Miami, FL" data-id="element-854" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-855">
              <div data-id="element-856">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-857">
                  Tipo de negocio *
                </label>
                <input type="text" name="business_type" required value={formData.business_type} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="Roofing, Salon, Cleaning..." data-id="element-858" />
              </div>
              <div data-id="element-859">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-860">
                  Nombre del negocio *
                </label>
                <input type="text" name="business_name" required value={formData.business_name} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="Mi Negocio LLC" data-id="element-861" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-862">
              <div data-id="element-863">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-864">
                  Instagram del negocio *
                </label>
                <input type="text" name="instagram" required value={formData.instagram} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="@tunegocio" data-id="element-865" />
              </div>
              <div data-id="element-866">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-867">
                  Ticket promedio por venta ($)
                </label>
                <input type="text" name="avg_ticket" value={formData.avg_ticket} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="$500" data-id="element-868" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-869">
              <div data-id="element-870">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-871">
                  ¿Cuántos clientes tienes por semana?
                </label>
                <input type="text" name="clients_per_week" value={formData.clients_per_week} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="5-10" data-id="element-872" />
              </div>
              <div data-id="element-873">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-874">
                  ¿Cuántos clientes nuevos necesitas por semana? *
                </label>
                <input type="text" name="new_clients_needed" required value={formData.new_clients_needed} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" placeholder="10-20" data-id="element-875" />
              </div>
            </div>

            <div className="mb-6" data-id="element-876">
              <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-877">
                ¿Has invertido en ads antes? *
              </label>
              <div className="flex gap-4" data-id="element-878">
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-879">
                  <input type="radio" name="ads_before" value="Yes" checked={formData.ads_before === 'Yes'} onChange={handleChange} className="w-4 h-4 text-[#D4A843] bg-[#0A0A0A] border-gray-700 focus:ring-[#D4A843]" data-id="element-880" />
                  <span className="text-gray-300" data-id="element-881">Sí</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-882">
                  <input type="radio" name="ads_before" value="No" checked={formData.ads_before === 'No'} onChange={handleChange} className="w-4 h-4 text-[#D4A843] bg-[#0A0A0A] border-gray-700 focus:ring-[#D4A843]" data-id="element-883" />
                  <span className="text-gray-300" data-id="element-884">No</span>
                </label>
              </div>
            </div>

            {formData.ads_before === 'Yes' && <div className="mb-6" data-id="element-885">
                <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-886">
                  ¿Qué paso?
                </label>
                <textarea name="ads_experience" value={formData.ads_experience} onChange={handleChange} rows={3} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors resize-none" placeholder="Cuéntanos brevemente tu experiencia..." data-id="element-887" />
              </div>}

            <div className="mb-8" data-id="element-888">
              <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-889">
                ¿Cuánto dinero están gastando en publicidad actualmente al mes?
                *
              </label>
              <select name="monthly_ad_spend_range" required value={formData.monthly_ad_spend_range} onChange={handleChange} className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none transition-colors" data-id="element-890">
                <option value="" data-id="element-891">Selecciona una opción</option>
                <option value="0-500" data-id="element-892">$0 - $500</option>
                <option value="500-1500" data-id="element-893">$500 - $1,500</option>
                <option value="1500-5000" data-id="element-894">$1,500 - $5,000</option>
                <option value="5000+" data-id="element-895">Más de $5,000</option>
              </select>
            </div>

            <div className="flex justify-center" data-id="element-896">
              <motion.button type="submit" disabled={sendStatus === 'sending'} whileHover={sendStatus === 'idle' ? {
              scale: 1.02
            } : {}} whileTap={sendStatus === 'idle' ? {
              scale: 0.98
            } : {}} className={`font-bold py-2.5 px-8 rounded-md text-sm border border-transparent transition-all duration-300 text-center whitespace-nowrap ${sendStatus === 'sent' ? 'bg-[#059669] text-white' : sendStatus === 'error' ? 'bg-red-500 text-white' : sendStatus === 'sending' ? 'gold-gradient text-black opacity-70 cursor-wait' : 'gold-gradient text-black emerald-glow-hover'}`} data-id="element-897">
                {sendStatus === 'sending' && 'ENVIANDO...'}
                {sendStatus === 'sent' && '✓ ¡SOLICITUD ENVIADA CON ÉXITO!'}
                {sendStatus === 'error' && '✕ ERROR — INTENTA DE NUEVO'}
                {sendStatus === 'idle' && 'OBTENER MI DIAGNÓSTICO GRATUITO'}
              </motion.button>
            </div>

            <div className="text-center mt-6" data-id="element-898">
              <div className="inline-flex items-center gap-3 bg-[#0A0A0A] border border-[#D4A843]/20 rounded-full px-6 py-2.5" data-id="element-899">
                <span className="text-gray-500 line-through text-sm" data-id="element-900">
                  Valor: $500 USD
                </span>
                <span className="text-[#059669] font-bold text-sm" data-id="element-901">
                  HOY: GRATIS
                </span>
              </div>
            </div>
          </motion.form>
        </motion.div>
      </BackgroundSection>
    </>;
}
function Footer() {
  const [footerName, setFooterName] = useState('');
  const [footerEmail, setFooterEmail] = useState('');
  const [footerPhone, setFooterPhone] = useState('');
  const [footerBusiness, setFooterBusiness] = useState('');
  const [footerStatus, setFooterStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [openResource, setOpenResource] = useState<number | null>(null);
  const certifications = [{
    src: 'https://cdn.magicpatterns.com/uploads/pBaETD69YuAZnEC2NMgyN6/IMG_0182.png',
    alt: 'Meta Business Partner'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/kjvZrE6BHjQdsLCQLEFT5k/IMG_0183.png',
    alt: 'Google Ads'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/tkQdMzRents4n11tVbsxCD/IMG_0184.jpg',
    alt: 'B2B Ecommerce Association'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/rk6HtZxNjdUiHRLfvC6PgD/IMG_0185.jpg',
    alt: 'Hortonworks Certified'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/oNPxbVhsgjZQZJeXEMQQkL/IMG_0181.webp',
    alt: 'Shopify Experts'
  }];
  const handleFooterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!footerName || !footerEmail || !footerPhone || !footerBusiness) return;
    setFooterStatus('sending');
    try {
      const footerParams = {
        nombre: footerName,
        email: footerEmail,
        negocio: footerBusiness,
        telefono: footerPhone,
        inversion: 'No especificado',
        mensaje: `👤 Nombre: ${footerName}\n📧 Email: ${footerEmail}\n📞 Teléfono: ${footerPhone}\n🏢 Empresa: ${footerBusiness}\n📍 Fuente: Footer Mini Form`,
        to_email: 'doctor.ecommerceus@gmail.com',
        page_url: window.location.href,
        date: new Date().toLocaleString()
      };
      // 1) Email para ADMIN
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ADMIN, footerParams, EMAILJS_PUBLIC_KEY);
      // 2) Email AUTO-REPLY para el CLIENTE
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_AUTOREPLY, footerParams, EMAILJS_PUBLIC_KEY);
      setFooterStatus('sent');
      setFooterName('');
      setFooterEmail('');
      setFooterPhone('');
      setFooterBusiness('');
      setTimeout(() => setFooterStatus('idle'), 3000);
    } catch (error) {
      setFooterStatus('error');
      setTimeout(() => setFooterStatus('idle'), 3000);
    }
  };
  const services = ['Meta Ads · Google Ads · TikTok Ads · YouTube Ads', 'Email · SMS · Retargeting Automatizado', 'Automatización con IA para Ventas 24/7', 'Funnels Quirúrgicos por Industria', 'Blindaje Digital & Anti-Ban', 'PR Digital & Posicionamiento en Medios', 'CRM & Data Tracking Avanzado', 'Video Marketing IA · SEO · Branding', 'Gestión de Reputación · Google Maps', 'Diseño Web · Landing Pages · CRO'];
  const resources = [{
    label: 'Blog de Estrategia',
    content: 'Próximamente publicaremos artículos, guías y análisis sobre estrategias de adquisición de clientes para negocios locales en USA. Desde cómo estructurar tu oferta hasta cómo escalar tus campañas publicitarias sin desperdiciar presupuesto. Suscríbete a nuestro diagnóstico gratuito para ser el primero en recibir contenido exclusivo.'
  }, {
    label: 'Casos de Éxito',
    content: 'Hemos ayudado a más de 1,877 negocios locales en Estados Unidos a conseguir clientes de forma predecible. Desde roofing en Houston hasta salones de belleza en Los Angeles, nuestro Sistema A.C.E.™ ha generado resultados medibles: más llamadas, más citas y más ventas. Visita la sección de resultados en esta página para ver testimonios reales de nuestros clientes.'
  }, {
    label: 'Política de Privacidad',
    content: 'En Doctor Ecommerce respetamos tu privacidad. La información que compartes con nosotros (nombre, teléfono, tipo de negocio, etc.) se utiliza exclusivamente para contactarte y ofrecerte nuestro diagnóstico gratuito. No vendemos, compartimos ni distribuimos tus datos personales a terceros bajo ninguna circunstancia. Utilizamos encriptación estándar de la industria para proteger tu información. Al enviar el formulario, aceptas que te contactemos por teléfono, SMS o email para agendar tu diagnóstico. Puedes solicitar la eliminación de tus datos en cualquier momento escribiendo a doctor.ecommerceus@gmail.com.'
  }, {
    label: 'Términos y Condiciones',
    content: 'Al utilizar los servicios de Doctor Ecommerce, aceptas los siguientes términos: (1) El diagnóstico gratuito es una consultoría inicial sin compromiso de compra. (2) Los resultados mostrados en testimonios son reales pero pueden variar según zona, industria, oferta y capacidad de respuesta del negocio. (3) La Garantía A.C.E.™ de 30 días aplica bajo las condiciones específicas detalladas en la sección de garantía de esta página. (4) Los precios y servicios están sujetos a cambios sin previo aviso. (5) Doctor Ecommerce se reserva el derecho de rechazar clientes que no cumplan con los criterios de calificación. Para consultas legales, contacta: doctor.ecommerceus@gmail.com | 402-872-0639 | 1001 Water St, Tampa FL 33602.'
  }];
  return <footer className="bg-[#070707]" data-id="element-902">
      {/* Gold gradient top border */}
      <div className="h-[2px] gold-gradient" data-id="element-903" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8" data-id="element-904">
        {/* ═══ MAIN FOOTER GRID — Desktop optimized ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 mb-14" data-id="element-905">
          {/* Logo + About + Social — Takes more space on desktop */}
          <div className="lg:col-span-4" data-id="element-906">
            <img src="https://cdn.magicpatterns.com/uploads/6TdzGPbST4y3Gk6CzyrcQT/IMG_6635.jpg" alt="Doctor Ecommerce Logo" className="h-14 mb-5" data-id="element-907" />
            <h3 className="gold-text font-bold text-xl mb-4" data-id="element-908">
              La cura para negocios estancados.
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-6" data-id="element-909">
              En Doctor Ecommerce no gestionamos redes, operamos sistemas de
              adquisición de alto rendimiento. Con sede estratégica en Tampa,
              Florida, servimos a dueños de negocios en todo Estados Unidos,
              transformando presupuestos publicitarios en máquinas de generación
              de llamadas y ventas diarias.
            </p>
            {/* Social Icons */}
            <div className="flex items-center gap-5" data-id="element-910">
              <a href="#" className="text-[#D4A843] hover:text-[#D4A843]/80 transition-colors" aria-label="Instagram" data-id="element-911">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-912">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" data-id="element-913" />
                </svg>
              </a>
              <a href="#" className="text-[#D4A843] hover:text-[#D4A843]/80 transition-colors" aria-label="Facebook" data-id="element-914">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-915">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-916" />
                </svg>
              </a>
              <a href="#" className="text-[#D4A843] hover:text-[#D4A843]/80 transition-colors" aria-label="LinkedIn" data-id="element-917">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-918">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-919" />
                </svg>
              </a>
              <a href="#" className="text-[#D4A843] hover:text-[#D4A843]/80 transition-colors" aria-label="TikTok" data-id="element-920">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-921">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" data-id="element-922" />
                </svg>
              </a>
            </div>
          </div>

          {/* Services */}
          <div className="lg:col-span-3" data-id="element-923">
            <h3 className="gold-text font-bold text-base mb-5 tracking-wide uppercase text-sm" data-id="element-924">
              Servicios
            </h3>
            <ul className="space-y-3" data-id="element-925">
              {services.map((service, index) => <li key={index} className="text-gray-400 text-sm leading-relaxed" data-id="element-926">
                  {service}
                </li>)}
            </ul>
          </div>

          {/* Resources & Legal */}
          <div className="lg:col-span-2" data-id="element-927">
            <h3 className="gold-text font-bold text-base mb-5 tracking-wide uppercase text-sm" data-id="element-928">
              Recursos
            </h3>
            <ul className="space-y-3" data-id="element-929">
              {resources.map((resource, index) => <li key={index} data-id="element-930">
                  <button onClick={() => setOpenResource(openResource === index ? null : index)} className="text-gray-400 text-sm hover:text-[#D4A843] transition-colors text-left cursor-pointer flex items-center gap-1.5 w-full" data-id="element-931">
                    <ChevronDownIcon className={`w-3 h-3 flex-shrink-0 transition-transform duration-300 ${openResource === index ? 'rotate-180 text-[#D4A843]' : ''}`} data-id="element-932" />
                    <span className={openResource === index ? 'text-[#D4A843]' : ''} data-id="element-933">
                      {resource.label}
                    </span>
                  </button>
                  {openResource === index && <div className="mt-2 ml-4.5 pl-3 border-l border-[#D4A843]/20 animate-[fadeIn_0.3s_ease-out]" data-id="element-934">
                      <p className="text-gray-500 text-xs leading-relaxed" data-id="element-935">
                        {resource.content}
                      </p>
                    </div>}
                </li>)}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3" data-id="element-936">
            <h3 className="gold-text font-bold text-base mb-5 tracking-wide uppercase text-sm" data-id="element-937">
              Contacto Directo
            </h3>
            <div className="space-y-3 mb-5" data-id="element-938">
              <div className="flex items-start gap-3" data-id="element-939">
                <MapPinIcon className="w-4 h-4 text-[#D4A843] flex-shrink-0 mt-0.5" data-id="element-940" />
                <span className="text-gray-400 text-sm" data-id="element-941">
                  1001 WATER ST, TAMPA FL 33602
                </span>
              </div>
              <a href="tel:+14028720639" className="flex items-center gap-3 text-gray-400 text-sm hover:text-[#D4A843] transition-colors" data-id="element-942">
                <PhoneIcon className="w-4 h-4 text-[#D4A843] flex-shrink-0" data-id="element-943" />
                <span data-id="element-944">402-872-0639</span>
              </a>
              <a href="mailto:doctor.ecommerceus@gmail.com" className="flex items-center gap-3 text-gray-400 text-sm hover:text-[#D4A843] transition-colors" data-id="element-945">
                <MailIcon className="w-4 h-4 text-[#D4A843] flex-shrink-0" data-id="element-946" />
                <span data-id="element-947">doctor.ecommerceus@gmail.com</span>
              </a>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2.5" data-id="element-948">
              <a href="https://wa.me/message/TVKVR7FDMKG3P1" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition-colors" data-id="element-949">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" data-id="element-950">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-951" />
                </svg>
                <span data-id="element-952">Habla con un Especialista</span>
              </a>
              <a href="#formulario" onClick={e => {
              e.preventDefault();
              const formSection = document.getElementById('formulario');
              if (formSection) {
                formSection.scrollIntoView({
                  behavior: 'smooth'
                });
              }
            }} className="flex items-center justify-center gap-2 w-full gold-gradient text-black font-bold py-2.5 px-4 rounded-lg text-sm transition-all hover:opacity-90 cursor-pointer" data-id="element-953">
                <CalendarIcon className="w-4 h-4" data-id="element-954" />
                <span data-id="element-955">RESERVAR DIAGNÓSTICO</span>
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent mb-10" data-id="element-956" />

        {/* ═══ Certifications + Mini Form — Side by side on desktop ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10" data-id="element-957">
          {/* Certifications */}
          <div className="group/certs rounded-2xl border border-transparent p-4 -m-4 transition-all duration-500 hover:border-[#10B981] hover:bg-[#059669]/[0.04] hover:shadow-[0_0_24px_rgba(5,150,105,0.2),inset_0_0_20px_rgba(5,150,105,0.03)]" data-id="element-958">
            <p className="text-gray-500 text-xs mb-5 uppercase tracking-widest group-hover/certs:text-[#10B981]/70 transition-colors duration-500" data-id="element-959">
              Partners Certificados y Tecnología de Élite
            </p>
            <div className="flex flex-wrap items-center gap-3" data-id="element-960">
              {certifications.map((cert, index) => <div key={index} className="bg-white rounded-lg p-1.5 h-10 md:h-12 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 group-hover/certs:shadow-[0_0_8px_rgba(5,150,105,0.15)]" data-id="element-961">
                  <img src={cert.src} alt={cert.alt} className="h-full w-auto object-contain max-w-[80px] md:max-w-[100px]" data-id="element-962" />
                </div>)}
            </div>
          </div>

          {/* Mini Contact Form */}
          <div data-id="element-963">
            <form onSubmit={handleFooterSubmit} data-id="element-964">
              <p className="text-gray-500 text-xs mb-5 uppercase tracking-widest" data-id="element-965">
                ¿Quieres que te contactemos?
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-2.5" data-id="element-966">
                <input type="text" placeholder="Tu Nombre" value={footerName} onChange={e => setFooterName(e.target.value)} required className="flex-1 min-w-[140px] px-4 py-2.5 bg-[#070707] border border-gray-800 rounded-lg text-white text-sm focus:border-[#D4A843] focus:outline-none transition-colors" data-id="element-967" />
                <input type="email" placeholder="Tu Email" value={footerEmail} onChange={e => setFooterEmail(e.target.value)} required className="flex-1 min-w-[140px] px-4 py-2.5 bg-[#070707] border border-gray-800 rounded-lg text-white text-sm focus:border-[#D4A843] focus:outline-none transition-colors" data-id="element-968" />
                <input type="tel" placeholder="Tu Teléfono" value={footerPhone} onChange={e => setFooterPhone(e.target.value)} required className="flex-1 min-w-[140px] px-4 py-2.5 bg-[#070707] border border-gray-800 rounded-lg text-white text-sm focus:border-[#D4A843] focus:outline-none transition-colors" data-id="element-969" />
                <input type="text" placeholder="Nombre de tu Empresa" value={footerBusiness} onChange={e => setFooterBusiness(e.target.value)} required className="flex-1 min-w-[140px] px-4 py-2.5 bg-[#070707] border border-gray-800 rounded-lg text-white text-sm focus:border-[#D4A843] focus:outline-none transition-colors" data-id="element-970" />
                <button type="submit" disabled={footerStatus === 'sending'} className={`px-6 py-2.5 rounded-lg font-bold text-sm transition-all flex-shrink-0 border border-transparent ${footerStatus === 'sent' ? 'bg-[#059669] text-white' : footerStatus === 'error' ? 'bg-red-500 text-white' : 'gold-gradient text-black emerald-glow-hover'}`} data-id="element-971">
                  {footerStatus === 'sending' ? '...' : footerStatus === 'sent' ? '✓' : footerStatus === 'error' ? '✕' : 'ENVIAR'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* ═══ Copyright ═══ */}
        <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent mb-6" data-id="element-972" />
        <p className="text-gray-600 text-xs text-center" data-id="element-973">
          © 2024 Doctor Ecommerce. Todos los derechos reservados. Built for
          High Performance.
        </p>
      </div>
    </footer>;
}
function FloatingMobileCTA() {
  return <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden" data-id="element-974">
      <a href="tel:+14028720639" className="flex items-center justify-center gap-2 w-full gold-gradient text-black font-bold py-4 text-lg shadow-[0_-4px_20px_rgba(212,168,67,0.3)]" data-id="element-975">
        <PhoneIcon className="w-5 h-5" data-id="element-976" />
        <span data-id="element-977">LLAMAR AHORA</span>
      </a>
    </div>;
}
export function LandingPage() {
  // Meta Pixel: Track PageView on mount
  useEffect(() => {
    if (window.fbq) window.fbq('track', 'PageView');
  }, []);
  return <div className="min-h-screen bg-[#0A0A0A]" data-id="element-978">
      <NavigationHeader data-id="element-979" />
      <HeroSection data-id="element-980" />
      <ProblemSection data-id="element-981" />
      <SystemSection data-id="element-982" />
      <IdentitySection data-id="element-983" />
      <ResultsSection data-id="element-984" />
      <MediaSection data-id="element-985" />
      <AntiGuruSection data-id="element-986" />
      <ServicesSection data-id="element-987" />
      <QualificationSection data-id="element-988" />
      <GuaranteeSection data-id="element-989" />
      <FAQSection data-id="element-990" />
      <FormSection data-id="element-991" />
      <Footer data-id="element-992" />
    </div>;
}